import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft, GitBranch, LockKey, Plus, Buildings,
    PaperPlaneTilt, CheckCircle, XCircle, PencilSimple, Printer, Trash, Sparkle,
    Paperclip, FileText, FileArrowUp, Eye, DownloadSimple, Info, ArrowClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError, previewDocument, downloadDocument } from "../lib/api";
import { formatINR, formatDate, formatDateTime } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import OwnerCell from "../components/OwnerCell";
import ReassignOwnerDialog from "../components/ReassignOwnerDialog";
import SearchableSelect from "../components/SearchableSelect";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import ProposalPackageContext, { isPackageLineDirty } from "../components/proposals/ProposalPackageContext";
import { CommitmentsEditor } from "../components/proposals/CommitmentsEditor";
import { CommitmentsBadgeStrip } from "../components/proposals/CommitmentsBadgeStrip";
import { CapabilityBadges } from "../components/catalogue/CapabilityBadges";
import { SpecialOfferEditor } from "../components/proposals/SpecialOfferEditor";
import { CommercialSummaryRows } from "../components/billing/CommercialSummary";
import RichTextEditor from "../components/RichTextEditor";
import { isHtmlContent, sanitizeNarrativeHtml } from "../lib/richText";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { cn } from "../lib/utils";
import CatalogueProductPicker from "../lib/CatalogueProductPicker";
import { expandLineItem, expandLineItems, formatScopeChannels, isPackageLine } from "../lib/proposalPackageDisplay";
import { usesAnnualCost } from "../lib/lineItemFees";
import { annualPeriodEndIso } from "../lib/servicePeriod";
import ProposalPoCoverage, { ConvertChargesDialog } from "../components/billing/ProposalPoCoverage";
import { DeliveryIcon } from "./proposal-builder-prototype/deliveryIcons";
import { DELIVERY_MODES } from "./proposal-builder-prototype/builderStorage";
import CustomizationsCard from "./proposals/CustomizationsCard";
import { toast } from "sonner";

const STATUS_BADGE_MAP = {
    Draft: { status: "Draft" },
    Sent: { status: "Open", className: "!bg-blue-50 !text-blue-700 !border-blue-200" },
    Approved: { status: "Locked", className: "!bg-emerald-50 !text-emerald-800 !border-emerald-200" },
    Converted: { status: "Active" },
    Rejected: { status: "Unpaid" },
    // 2026-07-22 · Proposal Versioning v2 — operator-initiated cancellation
    // of the whole proposal. Distinct from Rejected (which represents
    // client-side rejection). Renders in a muted slate tone so it does not
    // scream "error" in the list — cancellations are legitimate lifecycle
    // events.
    Cancelled: { status: "Closed", className: "!bg-slate-100 !text-slate-600 !border-slate-300" },
};

const emptyLine = {
    product_name: "", product_type: "One-time", amount: 0,
    billing_frequency: "Annual", gst_applicable: true, gst_percent: 18,
    start_date: "", end_date: "", option_label: "", option_description: "",
    hsn_sac_code: "997331",
    // Iter C.1.1 — Phase C package context, null/empty for atomic line items.
    package_source_id: null,
    package_source_code: null,
    package_presentation_mode: null,
    package_consolidated_label: null,
    package_components: [],
    // Iter C.2.1.d — Commercial Commitment Representation (R-B*).
    // Optional list of typed commercial commitments owned by this line's
    // Package. Empty list preserves the legacy (amount, amc_amount) shape.
    commitments: [],
};

// Iter 66.14 — Cap option labels in display surfaces so paragraph-length
// text doesn't render as a banner. Full text remains accessible via the
// HTML `title` tooltip and via the dedicated `option_description` field.
const OPTION_LABEL_DISPLAY_LIMIT = 80;
const truncateLabel = (s, limit = OPTION_LABEL_DISPLAY_LIMIT) => {
    if (!s) return s;
    const clean = String(s).trim();
    if (clean.length <= limit) return clean;
    return clean.slice(0, limit - 1).trimEnd() + "…";
};

/**
 * Version History table — 2026-07-22 · Proposal Versioning v2.
 *
 * Renders one row per version with the operator-requested columns:
 *   Version No. · Date & Time · Created By · Status · Revision Note.
 * Rows are sorted DESCENDING by version_number so the latest sits at the
 * top. The "Latest" pill is derived at render time from
 * `max(version_number)` among versions whose `status !== "cancelled"` —
 * there is no `superseded` business status; latest is a derived property.
 * Cancelled versions render with a muted row, a "Cancelled" pill, and
 * cancellation metadata beneath the revision note.
 */
function VersionTimeline({ proposal, selectedId, onSelect }) {
    const sorted = useMemo(
        () => [...(proposal.versions || [])].sort((a, b) => b.version_number - a.version_number),
        [proposal.versions]
    );
    const latestActiveNumber = useMemo(() => {
        const active = sorted.filter((v) => (v.status || "active") !== "cancelled");
        return active.length ? active[0].version_number : null; // sorted desc → first is max
    }, [sorted]);

    return (
        <div className="reedos-card overflow-hidden" data-testid="proposal-version-timeline">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><GitBranch size={12} /> Version history</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        {sorted.length} version{sorted.length > 1 ? "s" : ""}
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm" data-testid="proposal-version-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                            <th className="px-4 py-2.5 reedos-eyebrow w-[110px]">Version</th>
                            <th className="px-4 py-2.5 reedos-eyebrow w-[180px]">Date &amp; Time</th>
                            <th className="px-4 py-2.5 reedos-eyebrow w-[160px]">Created By</th>
                            <th className="px-4 py-2.5 reedos-eyebrow w-[160px]">Status</th>
                            <th className="px-4 py-2.5 reedos-eyebrow">Revision Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((v) => {
                            const isApproved = v.id === proposal.approved_version_id;
                            const isCancelled = (v.status || "active") === "cancelled";
                            const isLatest = v.version_number === latestActiveNumber;
                            const isSelected = v.id === selectedId;
                            return (
                                <tr
                                    key={v.id}
                                    onClick={() => onSelect(v.id)}
                                    data-testid={`proposal-version-row-${v.version_number}`}
                                    className={cn(
                                        "border-t border-slate-100 cursor-pointer transition-colors",
                                        isSelected ? "bg-slate-900 text-white" : "hover:bg-slate-50",
                                        isCancelled && !isSelected ? "opacity-70" : "",
                                    )}
                                >
                                    <td className="px-4 py-3">
                                        <div className={cn("font-mono text-sm font-bold", isSelected ? "text-white" : "text-slate-900")}>
                                            V{v.version_number}
                                        </div>
                                        {v.source_version_no != null && (
                                            <div className={cn("text-[10px] mt-0.5", isSelected ? "text-white/60" : "text-slate-400")}
                                                 data-testid={`proposal-version-lineage-${v.version_number}`}>
                                                from V{v.source_version_no}
                                            </div>
                                        )}
                                    </td>
                                    <td className={cn("px-4 py-3 text-xs", isSelected ? "text-white/80" : "text-slate-600")}>
                                        {formatDateTime(v.created_date)}
                                    </td>
                                    <td className={cn("px-4 py-3 text-xs", isSelected ? "text-white/80" : "text-slate-700")}>
                                        {v.created_by_name || v.created_by || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap items-center gap-1">
                                            {isLatest && (
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded",
                                                        isSelected ? "bg-white/20 text-white" : "bg-slate-900 text-white"
                                                    )}
                                                    data-testid={`proposal-version-latest-${v.version_number}`}
                                                >
                                                    LATEST
                                                </span>
                                            )}
                                            {isApproved && (
                                                <span className={cn(
                                                    "inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded",
                                                    isSelected ? "bg-white/20 text-white" : "bg-emerald-200 text-emerald-900"
                                                )}>
                                                    <LockKey size={9} weight="fill" /> APPROVED
                                                </span>
                                            )}
                                            {isCancelled && (
                                                <span
                                                    className={cn(
                                                        "inline-flex items-center text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded",
                                                        isSelected ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
                                                    )}
                                                    data-testid={`proposal-version-cancelled-${v.version_number}`}
                                                >
                                                    CANCELLED
                                                </span>
                                            )}
                                            {!isCancelled && !isApproved && !isLatest && (
                                                <span className={cn(
                                                    "inline-flex items-center text-[10px] font-medium tracking-wide px-1.5 py-0.5 rounded",
                                                    isSelected ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"
                                                )}>
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={cn("text-xs whitespace-normal", isSelected ? "text-white/90" : "text-slate-700")}>
                                            {v.revision_note || v.remarks || "—"}
                                        </div>
                                        {isCancelled && v.cancel_reason && (
                                            <div className={cn("text-[10px] mt-1", isSelected ? "text-white/60" : "text-slate-500")}>
                                                Cancelled: {v.cancel_reason}
                                                {v.cancelled_by_name ? ` · by ${v.cancelled_by_name}` : ""}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function TncSetCard({ proposal, version, editable, onChanged }) {
    const [sets, setSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // T&C reselection fix (2026-07-23) — resolve the displayed Full-mode
    // set from the currently-viewed version's `builder_state.tnc.fullTncId`
    // so historical versions retain their own T&C snapshot. Fall back to
    // the proposal-level `tnc_id` only when the version has no stored
    // `fullTncId` (legacy / pre-Builder versions).
    const currentId = version?.builder_state?.tnc?.fullTncId
        || proposal.tnc_id
        || "master";

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/billing/proposal-tnc/list?for_picker=1");
                setSets(data.items || []);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load T&C sets"));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const current = sets.find((s) => s.id === currentId);

    const change = async (newId) => {
        if (newId === currentId) return;
        setSaving(true);
        try {
            await api.patch(`/billing/proposal/${proposal.id}/tnc`, { tnc_id: newId });
            toast.success(`T&C switched to "${sets.find((s) => s.id === newId)?.name || newId}"`);
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to switch T&C set"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="reedos-card p-5" data-testid="proposal-tnc-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="reedos-eyebrow">Terms &amp; Conditions set</div>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                        {loading ? "Loading…" : (current?.name || (currentId === "master" ? "Default" : "(missing — falls back to default)"))}
                    </h3>
                    {current?.description && (
                        <p className="text-xs text-slate-500 mt-1">{current.description}</p>
                    )}
                    {current && (
                        <div className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-3">
                            <span>{current.clauses_count} clauses</span>
                            <span>·</span>
                            <span>{current.payment_terms_count} payment terms</span>
                            {current.is_default && (
                                <span className="inline-flex items-center gap-1 ml-1 rounded px-1.5 py-0.5 text-[9px] font-bold border bg-emerald-50 text-emerald-800 border-emerald-200">DEFAULT</span>
                            )}
                        </div>
                    )}
                    {/* PB-004 (V1-4.T&C) — Show the effective T&C mode configured on
                        the proposal so the operator sees what the client-facing PDF
                        will actually render. Full = master set (default). Reference /
                        Reference + Additional = short reference statement (+ operator
                        clauses). */}
                    {(() => {
                        const mode = proposal.tnc_mode || "full";
                        if (mode === "full") return null;
                        const refNo = proposal.tnc_reference_proposal_number || "—";
                        const clauseCount = Array.isArray(proposal.tnc_additional_clauses) ? proposal.tnc_additional_clauses.length : 0;
                        return (
                            <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/50 px-3 py-2 text-[11px] text-indigo-900" data-testid="proposal-tnc-mode-summary" data-tnc-mode={mode}>
                                <div className="font-semibold uppercase tracking-wide">
                                    Effective PDF mode:{" "}
                                    <span data-testid="proposal-tnc-mode-label">
                                        {mode === "reference" ? "Reference earlier proposal" : "Reference + Additional clauses"}
                                    </span>
                                </div>
                                <div className="mt-0.5 text-indigo-800">
                                    Reference: <span className="font-mono">{refNo}</span>
                                    {mode === "reference_plus_additional" && (
                                        <> · <span data-testid="proposal-tnc-additional-count">{clauseCount} additional {clauseCount === 1 ? "clause" : "clauses"}</span></>
                                    )}
                                </div>
                                <div className="mt-0.5 text-indigo-700 text-[10px] italic">
                                    Master T&amp;C set is NOT rendered in this mode. Configure via the Builder → Section 4.
                                </div>
                            </div>
                        );
                    })()}
                </div>
                {editable && !loading && (
                    <RoleGuard permission="po:write">
                        <select
                            value={currentId}
                            onChange={(e) => change(e.target.value)}
                            disabled={saving}
                            className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white min-w-[220px]"
                            data-testid="proposal-tnc-select"
                        >
                            {sets.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}{s.is_default ? " (default)" : ""}
                                </option>
                            ))}
                        </select>
                    </RoleGuard>
                )}
            </div>
        </div>
    );
}

function SignedProposalCard({ proposal, onChanged }) {
    // Iter 67 — Upload the actual counter-signed proposal PDF (mirrors the
    // PO `client_po_document` pattern). Editable in any status so operators
    // can backfill historical proposals signed before BCMS adoption.
    const [uploading, setUploading] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) {
            toast.error("File exceeds 15 MB. Compress the scan or split into pages.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("entity_type", "proposal");
            fd.append("entity_id", proposal.id);
            const { data: doc } = await api.post("/billing/document/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await api.patch(`/billing/proposal/${proposal.id}/signed-doc`, {
                signed_proposal_document_id: doc.id,
            });
            toast.success("Signed proposal attached");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setUploading(false);
        }
    };

    const detach = async () => {
        if (!window.confirm("Remove the attached signed proposal? The file remains on the server but will no longer be linked to this proposal.")) return;
        try {
            await api.patch(`/billing/proposal/${proposal.id}/signed-doc`, {
                signed_proposal_document_id: null,
            });
            toast.success("Signed proposal unlinked");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to unlink"));
        }
    };

    const doc = proposal.signed_proposal_document;

    return (
        <div className="reedos-card p-5" data-testid="proposal-signed-doc-card">
            <div className="reedos-eyebrow flex items-center gap-1.5 mb-3">
                <Paperclip size={11} /> Signed Proposal Document
                <span className="text-[10px] font-normal normal-case text-slate-400">
                    · counter-signed PDF / scan (internal, not shown on client print)
                </span>
            </div>
            {doc ? (
                <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="proposal-signed-doc-attached">
                    <FileText size={20} className="text-slate-500 shrink-0" weight="duotone" />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{doc.filename}</div>
                        <div className="text-[11px] text-slate-500">
                            {(doc.size / 1024).toFixed(1)} KB · uploaded {formatDateTime(doc.uploaded_at)}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={async () => {
                            try { await previewDocument(doc.id, { mime: doc.content_type }); }
                            catch (err) { toast.error(formatApiError(err, "Preview failed")); }
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-400 transition-colors"
                        data-testid="proposal-signed-doc-preview-btn"
                    >
                        <Eye size={12} /> Preview
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            try { await downloadDocument(doc.id, doc.filename); }
                            catch (err) { toast.error(formatApiError(err, "Download failed")); }
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-400 transition-colors"
                        data-testid="proposal-signed-doc-download-btn"
                    >
                        <DownloadSimple size={12} /> Download
                    </button>
                    <RoleGuard permission="proposal:write">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={detach}
                            className="h-8 px-2 text-slate-400 hover:text-rose-600"
                            title="Unlink document"
                            data-testid="proposal-signed-doc-remove-btn"
                        >
                            <Trash size={13} />
                        </Button>
                    </RoleGuard>
                </div>
            ) : (
                <RoleGuard permission="proposal:write">
                    <label
                        className="block border-2 border-dashed border-slate-200 bg-slate-50 rounded-md px-4 py-4 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-100 transition-colors"
                        data-testid="proposal-signed-doc-upload-zone"
                    >
                        <FileArrowUp size={18} className="mx-auto text-slate-400" />
                        <div className="text-xs text-slate-600 mt-1.5 font-medium">
                            {uploading ? "Uploading signed proposal…" : "Upload signed proposal PDF / scan"}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">PDF / JPG / PNG · max 15 MB</div>
                        <input
                            type="file"
                            className="hidden"
                            onChange={(e) => upload(e.target.files?.[0])}
                            accept=".pdf,image/*"
                            disabled={uploading}
                            data-testid="proposal-signed-doc-upload-input"
                        />
                    </label>
                </RoleGuard>
            )}
            {!doc && (
                <p className="text-[11px] text-slate-400 italic mt-2">
                    Tip: attach the counter-signed PDF so audit + sales always have the source artefact one click away. Works for historical proposals too.
                </p>
            )}
        </div>
    );
}



function HeaderEditor({ proposalId, version, editable, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(version.header_text || "");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { setText(version.header_text || ""); setEditing(false); }, [version.id]);

    const save = async () => {
        setSubmitting(true);
        try {
            await api.patch(`/billing/proposal/${proposalId}/header?version_id=${version.id}`, { header_text: text });
            toast.success("Header saved");
            setEditing(false);
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save header"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="reedos-card" data-testid="proposal-header-card">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                    <div className="reedos-eyebrow">Header / Scope</div>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Proposal narrative</h3>
                </div>
                {editable && !editing && (
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1.5" data-testid="edit-header-btn">
                        <PencilSimple size={14} /> Edit
                    </Button>
                )}
            </div>
            <div className="p-5">
                {editing ? (
                    <>
                        <RichTextEditor
                            value={text}
                            onChange={setText}
                            minHeight={220}
                            testId="header-editor"
                        />
                        <div className="flex justify-end gap-2 mt-3">
                            <Button variant="outline" onClick={() => { setEditing(false); setText(version.header_text || ""); }}>Cancel</Button>
                            <Button onClick={save} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="header-save-btn">
                                {submitting ? "Saving..." : "Save header"}
                            </Button>
                        </div>
                    </>
                ) : (
                    isHtmlContent(version.header_text) ? (
                        <div
                            className="proposal-narrative-view text-sm text-slate-700"
                            data-testid="header-text"
                            // Content is sanitized on both the write path
                            // (backend `html_sanitizer.sanitize_narrative_html`)
                            // and the read path (`sanitizeNarrativeHtml` via
                            // DOMPurify) — see `richText.js`.
                            dangerouslySetInnerHTML={{ __html: sanitizeNarrativeHtml(version.header_text) || "" }}
                        />
                    ) : (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="header-text">
                            {version.header_text || <span className="text-slate-400 italic">No header text yet.</span>}
                        </p>
                    )
                )}
            </div>
        </div>
    );
}

function ApplyTemplateDialog({ proposalId, versionId, onApplied }) {
    const [open, setOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [templateId, setTemplateId] = useState("");
    const [mode, setMode] = useState("append");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef(JSON.stringify({ templateId: "", mode: "append" }));

    useEffect(() => {
        if (!open) {
            setTemplateId("");
            setMode("append");
            return;
        }
        baselineRef.current = JSON.stringify({ templateId: "", mode: "append" });
        setLoading(true);
        api.get("/billing/proposal-template/list")
            .then(({ data }) => setTemplates((data.items || []).filter((t) => t.is_active)))
            .catch((err) => toast.error(formatApiError(err, "Failed to load templates")))
            .finally(() => setLoading(false));
    }, [open]);

    const isDirty = JSON.stringify({ templateId, mode }) !== baselineRef.current;

    const submit = async () => {
        if (!templateId) {
            toast.error("Pick a template first");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(
                `/billing/proposal/${proposalId}/apply-template?version_id=${versionId}`,
                { template_id: templateId, mode },
            );
            const tplName = templates.find((t) => t.id === templateId)?.name || "template";
            toast.success(
                mode === "replace"
                    ? `Version replaced with "${tplName}" — edit as needed`
                    : `Appended "${tplName}" line items to this version`,
            );
            baselineRef.current = JSON.stringify({ templateId, mode });  // bypass discard prompt
            setOpen(false);
            onApplied && onApplied();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to apply template"));
        } finally {
            setSubmitting(false);
        }
    };

    const picked = templates.find((t) => t.id === templateId);

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                onClick={() => setOpen(true)}
                className="gap-1.5"
                data-testid="proposal-apply-template-btn"
            >
                <Sparkle size={14} weight="bold" /> Apply template
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                contentClassName="max-w-lg"
                testId="proposal-apply-template-dialog"
                title={<span className="flex items-center gap-2"><Sparkle size={16} weight="bold" /> Apply proposal template</span>}
                description='Pre-fill this Draft version from a saved template. Useful after Lead→Proposal conversion when you want a structured starting point.'
            >
                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs font-semibold">Template</Label>
                        <SearchableSelect
                            options={templates.map((t) => ({
                                value: t.id,
                                label: t.name,
                                hint: `${t.module_code || "—"} · ${(t.line_items || []).length} items`,
                            }))}
                            value={templateId}
                            onChange={setTemplateId}
                            placeholder={loading ? "Loading templates…" : "— Select template —"}
                            searchPlaceholder="Type to filter templates…"
                            emptyText="No active template found."
                            testId="proposal-apply-template-picker"
                            className="mt-1.5"
                            disabled={loading || submitting}
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Apply mode</Label>
                        <div className="mt-2 grid grid-cols-1 gap-2">
                            <label
                                className={cn(
                                    "flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors",
                                    mode === "append"
                                        ? "border-slate-900 bg-slate-50"
                                        : "border-slate-200 hover:bg-slate-50",
                                )}
                                data-testid="proposal-apply-template-mode-append"
                            >
                                <input
                                    type="radio"
                                    name="apply-mode"
                                    checked={mode === "append"}
                                    onChange={() => setMode("append")}
                                    className="mt-0.5"
                                />
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-900">Append</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Adds template line items to your existing list. Header &amp; T&amp;C untouched.
                                        <span className="font-medium text-slate-700"> Safe default.</span>
                                    </div>
                                </div>
                            </label>
                            <label
                                className={cn(
                                    "flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors",
                                    mode === "replace"
                                        ? "border-slate-900 bg-slate-50"
                                        : "border-slate-200 hover:bg-slate-50",
                                )}
                                data-testid="proposal-apply-template-mode-replace"
                            >
                                <input
                                    type="radio"
                                    name="apply-mode"
                                    checked={mode === "replace"}
                                    onChange={() => setMode("replace")}
                                    className="mt-0.5"
                                />
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-900">Replace</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Wipes current line items and header on this version, then copies everything
                                        from the template (header text, T&amp;C, line items). Can't be undone.
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>
                    {picked && (
                        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                            <span className="reedos-eyebrow text-[9px]">Preview</span>
                            <div className="mt-1 text-slate-900 font-medium">{picked.name}</div>
                            <div className="mt-0.5 text-[11px]">
                                {(picked.line_items || []).length} line items{" "}
                                {picked.module_code ? `· ${picked.module_code}` : ""}
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <FormDialogCancel variant="ghost" disabled={submitting} testId="proposal-apply-template-cancel">
                        Cancel
                    </FormDialogCancel>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={!templateId || submitting}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="proposal-apply-template-submit"
                    >
                        {submitting ? "Applying…" : mode === "replace" ? "Replace with template" : "Append from template"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}


function BulkAddFromCatalogueDialog({ proposalId, versionId, onAdded, currency }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    // Map of catalogue id -> { picked, software_fee, amc_fee, gst_percent }
    const [picks, setPicks] = useState(new Map());
    const [optionLabel, setOptionLabel] = useState("");
    // Iter 66.14 — shared description for the whole batch (paragraph-length).
    const [optionDescription, setOptionDescription] = useState("");
    const [productType, setProductType] = useState("One-time");
    const [billingFrequency, setBillingFrequency] = useState("Annual");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef("");

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setPicks(new Map());
        setOptionLabel("");
        setOptionDescription("");
        setProductType("One-time");
        setBillingFrequency("Annual");
        baselineRef.current = JSON.stringify({
            picks: [], optionLabel: "", optionDescription: "", productType: "One-time", billingFrequency: "Annual",
        });
        (async () => {
            try {
                const { data } = await api.get("/billing/catalogue/list");
                setItems(data.items || []);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load catalogue"));
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    // Serialize Map → array for stable comparison
    const isDirty = JSON.stringify({
        picks: Array.from(picks.entries()),
        optionLabel,
        optionDescription,
        productType,
        billingFrequency,
    }) !== baselineRef.current;

    const filtered = useMemo(() => {
        if (!filter.trim()) return items;
        const n = filter.trim().toLowerCase();
        return items.filter((i) =>
            i.name.toLowerCase().includes(n) || (i.subtitle || "").toLowerCase().includes(n));
    }, [items, filter]);

    const togglePick = (item) => {
        setPicks((prev) => {
            const next = new Map(prev);
            if (next.has(item.id)) {
                next.delete(item.id);
            } else {
                next.set(item.id, {
                    software_fee: Number(item.default_software_fee) || 0,
                    amc_fee: Number(item.default_amc_fee) || 0,
                    gst_percent: Number(item.default_gst_percent) || 18,
                });
            }
            return next;
        });
    };
    const updatePick = (id, key, value) => {
        setPicks((prev) => {
            const next = new Map(prev);
            const row = next.get(id);
            if (!row) return prev;
            next.set(id, { ...row, [key]: value });
            return next;
        });
    };

    const totals = useMemo(() => {
        let sw = 0, amc = 0, tax = 0;
        for (const [id, row] of picks.entries()) {
            const sf = Number(row.software_fee) || 0;
            const af = Number(row.amc_fee) || 0;
            const g = Number(row.gst_percent) || 0;
            sw += sf;
            amc += af;
            tax += (sf + af) * g / 100;
        }
        return { sw, amc, tax, total: sw + amc + tax };
    }, [picks]);

    const submit = async () => {
        if (picks.size === 0) {
            toast.error("Pick at least one product or service");
            return;
        }
        const lines = [];
        for (const [id, row] of picks.entries()) {
            const item = items.find((i) => i.id === id);
            if (!item) continue;
            lines.push({
                product_name: item.name,
                product_type: productType,
                billing_frequency: billingFrequency,
                amount: Number(row.software_fee) || 0,
                amc_amount: Number(row.amc_fee) || 0,
                gst_applicable: true,
                gst_percent: Number(row.gst_percent) || 0,
                option_label: null,  // backend applies shared label from payload
            });
        }
        setSubmitting(true);
        try {
            await api.post(
                `/billing/proposal/${proposalId}/lineitem/bulk-add?version_id=${versionId}`,
                {
                    option_label: optionLabel.trim() || null,
                    option_description: optionDescription.trim() || null,
                    line_items: lines,
                },
            );
            toast.success(`Added ${lines.length} line item${lines.length !== 1 ? "s" : ""}`);
            baselineRef.current = JSON.stringify({
                picks: Array.from(picks.entries()),
                optionLabel,
                optionDescription,
                productType,
                billingFrequency,
            });  // bypass discard prompt
            setOpen(false);
            onAdded?.();
        } catch (err) {
            toast.error(formatApiError(err, "Bulk add failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                size="sm"
                onClick={() => setOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                data-testid="proposal-bulk-add-btn"
            >
                <Plus size={14} weight="bold" /> Bulk add from catalogue
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                contentClassName="max-w-3xl max-h-[88vh] flex flex-col"
                testId="proposal-bulk-add-dialog"
                title="Bulk add from catalogue"
                description="Pick products / services and edit Software & Set-Up Fee + AMC Fee per row. All picked items are added in a single batch with the same Offer Basket label (optional)."
            >

                <div className="flex items-center gap-2 flex-wrap">
                    <Input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Filter catalogue…"
                        className="max-w-xs"
                        data-testid="bulk-add-filter-input"
                    />
                    <div className="ml-auto text-[11px] text-slate-500">
                        {picks.size} picked of {items.length}
                    </div>
                </div>

                <div className="border border-slate-200 rounded-md overflow-y-auto flex-1 min-h-[280px]" data-testid="bulk-add-list">
                    {loading ? (
                        <div className="p-6 text-sm text-slate-500">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-6 text-sm text-slate-500 text-center">No catalogue items match.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-3 py-2 reedos-eyebrow w-8"></th>
                                    <th className="px-3 py-2 reedos-eyebrow">Product / Service</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-right w-32">Software Fee</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-right w-32">AMC Fee</th>
                                    <th className="px-3 py-2 reedos-eyebrow text-right w-20">GST</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((it) => {
                                    const picked = picks.get(it.id);
                                    return (
                                        <tr
                                            key={it.id}
                                            className={`border-t border-slate-100 ${picked ? "bg-slate-900/[.03]" : ""}`}
                                            data-testid={`bulk-row-${it.id}`}
                                        >
                                            <td className="px-3 py-2 align-top">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(picked)}
                                                    onChange={() => togglePick(it)}
                                                    className="w-4 h-4 cursor-pointer accent-slate-900"
                                                    data-testid={`bulk-row-check-${it.id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div className="font-semibold text-slate-900">{it.name}</div>
                                                {it.subtitle && (
                                                    <div className="text-[11px] text-slate-500 mt-0.5">{it.subtitle}</div>
                                                )}
                                                <div className="text-[10px] mt-0.5">
                                                    <span className={`inline-flex items-center rounded px-1.5 py-0 text-[9px] font-semibold border ${it.kind === "product" ? "bg-indigo-50 text-indigo-800 border-indigo-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                                                        {it.kind}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="100"
                                                    value={picked ? picked.software_fee : (it.default_software_fee || 0)}
                                                    onChange={(e) => {
                                                        if (!picked) togglePick(it);
                                                        updatePick(it.id, "software_fee", e.target.value);
                                                    }}
                                                    disabled={!picked}
                                                    className="font-mono text-right h-9 disabled:opacity-50"
                                                    data-testid={`bulk-row-sw-${it.id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="100"
                                                    value={picked ? picked.amc_fee : (it.default_amc_fee || 0)}
                                                    onChange={(e) => {
                                                        if (!picked) togglePick(it);
                                                        updatePick(it.id, "amc_fee", e.target.value);
                                                    }}
                                                    disabled={!picked}
                                                    className="font-mono text-right h-9 disabled:opacity-50"
                                                    data-testid={`bulk-row-amc-${it.id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <select
                                                    value={picked ? picked.gst_percent : (it.default_gst_percent ?? 18)}
                                                    onChange={(e) => {
                                                        if (!picked) togglePick(it);
                                                        updatePick(it.id, "gst_percent", Number(e.target.value));
                                                    }}
                                                    disabled={!picked}
                                                    className="w-full h-9 px-2 text-sm border border-slate-200 rounded-md bg-white disabled:opacity-50"
                                                    data-testid={`bulk-row-gst-${it.id}`}
                                                >
                                                    {[0, 5, 12, 18, 28].map((g) => (
                                                        <option key={g} value={g}>{g}%</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-3 pt-1" data-testid="bulk-add-controls">
                    <div>
                        <Label className="text-xs font-semibold">Type</Label>
                        <select value={productType} onChange={(e) => setProductType(e.target.value)} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white">
                            <option>One-time</option>
                            <option>AMC</option>
                        </select>
                    </div>
                    {/* Iter 51.8 — Frequency is meaningless for One-time items; hide the field
                        entirely when Type=One-time so the operator can't enter contradictory data. */}
                    {productType !== "One-time" ? (
                        <div>
                            <Label className="text-xs font-semibold">Billing frequency</Label>
                            <select value={billingFrequency} onChange={(e) => setBillingFrequency(e.target.value)} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white">
                                <option>Annual</option>
                                <option>Half-Yearly</option>
                                <option>Quarterly</option>
                                <option>Monthly</option>
                            </select>
                        </div>
                    ) : (
                        <div className="text-[10px] text-slate-400 pt-7 italic">
                            Frequency does not apply to One-time items.
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-semibold">Option label / Offer Basket <span className="font-normal text-slate-400">(optional · short)</span></Label>
                        <Input
                            value={optionLabel}
                            onChange={(e) => setOptionLabel(e.target.value.slice(0, 80))}
                            placeholder='e.g. "Combined Offering"'
                            className="mt-1.5"
                            maxLength={80}
                            data-testid="bulk-add-option-label"
                        />
                    </div>
                </div>
                {/* Iter 66.14 — Shared description applied to every line in the batch.
                    Rendered ONCE under the section header on the proposal items table. */}
                <div>
                    <Label className="text-xs font-semibold">Option description <span className="font-normal text-slate-400">(optional · paragraph)</span></Label>
                    <Textarea
                        value={optionDescription}
                        onChange={(e) => setOptionDescription(e.target.value)}
                        placeholder='Detailed scope / exclusivity / data-access clauses for the whole basket.'
                        rows={2}
                        className="mt-1.5"
                        data-testid="bulk-add-option-description"
                    />
                </div>

                <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 grid grid-cols-4 gap-3 text-xs" data-testid="bulk-add-totals">
                    <div>
                        <div className="reedos-eyebrow">Software fees</div>
                        <div className="reedos-data font-semibold mt-0.5">{formatINR(totals.sw, currency)}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">AMC fees</div>
                        <div className="reedos-data font-semibold mt-0.5">{formatINR(totals.amc, currency)}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">GST</div>
                        <div className="reedos-data font-semibold mt-0.5">{formatINR(totals.tax, currency)}</div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow">Total</div>
                        <div className="reedos-data font-display font-bold text-base mt-0.5 text-slate-900">{formatINR(totals.total, currency)}</div>
                    </div>
                </div>

                <DialogFooter>
                    <FormDialogCancel testId="bulk-add-cancel-btn">Cancel</FormDialogCancel>
                    <Button
                        onClick={submit}
                        disabled={submitting || picks.size === 0}
                        className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                        data-testid="bulk-add-submit-btn"
                    >
                        <Plus size={14} weight="bold" />
                        {submitting ? "Adding…" : `Add ${picks.size || 0} line item${picks.size !== 1 ? "s" : ""}`}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

function AddLineItemDialog({ proposalId, versionId, onAdded, mode = "create", initial, open: openProp, onOpenChange }) {
    const isEdit = mode === "edit";
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = typeof openProp === "boolean" && typeof onOpenChange === "function";
    const open = isControlled ? openProp : internalOpen;
    const setOpen = isControlled ? onOpenChange : setInternalOpen;

    const [form, setForm] = useState(emptyLine);
    const [submitting, setSubmitting] = useState(false);
    // Iter C.1.1 — atomic masters list for the package component picker.
    // Loaded once on dialog open; used by ProposalPackageContext.
    const [atomicMasters, setAtomicMasters] = useState([]);
    const baselineRef = useRef(JSON.stringify(emptyLine));

    useEffect(() => {
        if (!open) return;
        let next;
        if (isEdit && initial) {
            next = {
                product_name: initial.product_name || "",
                product_type: initial.product_type || "One-time",
                amount: initial.amount ?? 0,
                amc_amount: initial.amc_amount ?? 0,
                billing_frequency: initial.billing_frequency || "Annual",
                gst_applicable: initial.gst_applicable !== false,
                gst_percent: initial.gst_percent ?? 18,
                start_date: initial.start_date || "",
                end_date: initial.end_date || "",
                option_label: initial.option_label || "",
                option_description: initial.option_description || "",
                hsn_sac_code: initial.hsn_sac_code || "997331",
                // Iter C.1.1 — load package context if present
                package_source_id: initial.package_source_id || null,
                package_source_code: initial.package_source_code || null,
                package_presentation_mode: initial.package_presentation_mode || null,
                package_consolidated_label: initial.package_consolidated_label || null,
                package_components: initial.package_components || [],
                // Iter C.2.1.d — load commitments if present on the line.
                commitments: initial.commitments || [],
            };
        } else {
            next = emptyLine;
        }
        setForm(next);
        baselineRef.current = JSON.stringify(next);
    }, [open, isEdit, initial]);

    // Iter C.1.1 — Load atomic masters when the dialog opens, so the
    // package picker has a populated list. Filters out package items
    // (D1 — components can only FK to atomic).
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/billing/catalogue/list?limit=500");
                if (cancelled) return;
                setAtomicMasters(
                    (data.items || [])
                        .filter((it) => (it.package_kind || "atomic") === "atomic")
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
                );
            } catch {
                setAtomicMasters([]);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const submit = async (e) => {
        e.preventDefault();
        // Iter C.1.1 — D3 / D1 guard: any package row with an unset master,
        // missing code/label, or unset activation_rule blocks save with a
        // toast prompt rather than a silent rejection from the backend.
        if (form.package_source_id && isPackageLineDirty(form.package_components || [])) {
            toast.error(
                "Package line not ready",
                {
                    description: "Every component needs a master record, code, label and activation rule. Amber-highlighted rows still need attention.",
                },
            );
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                amount: Number(form.amount),
                amc_amount: Number(form.amc_amount || 0),
                gst_percent: Number(form.gst_percent || 0),
                start_date: form.start_date || null,
                end_date: form.end_date || null,
                option_label: form.option_label?.trim() || null,
                // Iter C.1.1 — Phase C package context. If the line item
                // is atomic these stay null/empty, matching the existing
                // legacy path on the backend.
                package_source_id: form.package_source_id || null,
                package_source_code: form.package_source_code || null,
                package_presentation_mode: form.package_source_id
                    ? (form.package_presentation_mode || "itemized")
                    : null,
                package_consolidated_label: form.package_consolidated_label?.trim() || null,
                package_components: form.package_source_id
                    ? (form.package_components || []).map((c) => ({
                        source_catalogue_id: (c.source_catalogue_id || "").trim(),
                        component_code: (c.component_code || "").trim(),
                        label: (c.label || "").trim(),
                        fee_kind: c.fee_kind,
                        amount: Number(c.amount) || 0,
                        billing_frequency: c.billing_frequency,
                        activation_rule: c.activation_rule,
                        free_support_months: c.activation_rule === "project_completion_plus_free_support"
                            ? (Number(c.free_support_months) || null)
                            : null,
                        gst_percent: Number(c.gst_percent ?? 18),
                        hsn_sac_code: c.hsn_sac_code || "997331",
                        is_visible_in_print: c.is_visible_in_print !== false,
                        description: c.description ?? null,
                        // Iter C.1.2c — Persist delivery surfaces onto the
                        // proposal snapshot. Normalise empty to null.
                        delivery_channels: Array.isArray(c.delivery_channels) && c.delivery_channels.length
                            ? c.delivery_channels
                            : null,
                    }))
                    : [],
                // Iter C.2.1.d — Persist authored commitments. Server fills
                // missing ids and auto-syncs line.amount to the sum.
                commitments: (form.commitments || []).map((c) => ({
                    id: c.id || null,
                    label: (c.label || "").trim(),
                    amount: Number(c.amount) || 0,
                    cadence: c.cadence || null,
                    gst_percent: Number(c.gst_percent ?? 18),
                    hsn_sac_code: c.hsn_sac_code || "997331",
                    activation_rule: c.activation_rule || null,
                    description: c.description || null,
                })),
            };
            if (isEdit) {
                await api.patch(
                    `/billing/proposal/${proposalId}/lineitem/${initial.id}?version_id=${versionId}`,
                    payload,
                );
                toast.success("Line item updated");
            } else {
                await api.post(`/billing/proposal/${proposalId}/lineitem/add?version_id=${versionId}`, payload);
                toast.success("Line item added");
            }
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            setOpen(false);
            if (!isEdit) setForm(emptyLine);
            onAdded?.();
        } catch (err) {
            toast.error(formatApiError(err, isEdit ? "Failed to update line item" : "Failed to add line item"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            {!isControlled && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpen(true)}
                    className="gap-1.5"
                    data-testid="proposal-add-line-btn"
                >
                    <Plus size={14} weight="bold" /> Add line item
                </Button>
            )}
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId="proposal-line-dialog"
                contentClassName={form.package_source_id ? "max-w-5xl" : undefined}
                title={isEdit ? "Edit line item" : "Add line item"}
            >
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold">Product / service</Label>
                        <div className="mt-1.5">
                            <CatalogueProductPicker
                                value={form.product_name}
                                onChange={(v, item) => setForm((p) => {
                                    const isPackage = item && (item.package_kind || "atomic") === "package";
                                    if (isPackage) {
                                        // Iter C.1.1 — Inflate the package: pre-populate
                                        // `package_components` from the catalogue's component
                                        // defaults so the operator sees the structure
                                        // immediately. Per D1 source_catalogue_id is FK'd to
                                        // master data; per D2 presentation_mode is per-line
                                        // (defaults to the catalogue's suggestion).
                                        return {
                                            ...p,
                                            product_name: v,
                                            product_type: "Package",
                                            hsn_sac_code: item.hsn_sac_code || p.hsn_sac_code || "997331",
                                            gst_percent: item.default_gst_percent ?? p.gst_percent,
                                            package_source_id: item.id,
                                            package_source_code: item.code || null,
                                            package_presentation_mode: item.default_presentation_mode || "itemized",
                                            package_consolidated_label: item.default_consolidated_label || null,
                                            // Iter C.2.1.CAP — Snapshot capabilities from the
                                            // catalogue master onto the proposal line at pick time.
                                            capabilities: Array.isArray(item.capabilities) ? [...item.capabilities] : [],
                                            package_components: (item.components || []).map((c) => ({
                                                source_catalogue_id: c.source_catalogue_id || "",
                                                component_code: c.component_code || "",
                                                label: c.label || "",
                                                fee_kind: c.fee_kind || "other",
                                                amount: Number(c.default_amount) || 0,
                                                billing_frequency: c.billing_frequency || "One-time",
                                                activation_rule: c.activation_rule || "immediate",
                                                free_support_months: c.free_support_months ?? null,
                                                gst_percent: c.gst_percent_override ?? item.default_gst_percent ?? 18,
                                                hsn_sac_code: c.hsn_sac_code_override || item.hsn_sac_code || "997331",
                                                is_visible_in_print: c.is_visible_in_print_default !== false
                                                    && ((item.default_presentation_mode || "itemized") !== "hybrid"
                                                        || (item.default_hybrid_visible_components || []).includes(c.component_code)),
                                                description: c.description || null,
                                                // Iter C.1.2c — Propagate delivery surfaces from
                                                // master onto the proposal snapshot. Multi-valued
                                                // metadata; null when the master is channel-agnostic.
                                                delivery_channels: Array.isArray(c.delivery_channels) && c.delivery_channels.length
                                                    ? c.delivery_channels
                                                    : null,
                                            })),
                                        };
                                    }
                                    return {
                                        ...p,
                                        product_name: v,
                                        // Iter 38: auto-fill HSN/SAC + GST from catalogue
                                        ...(item ? {
                                            hsn_sac_code: item.hsn_sac_code || p.hsn_sac_code || "997331",
                                            gst_percent: item.default_gst_percent ?? p.gst_percent,
                                            // Iter C.2.1.CAP — Snapshot capabilities from the
                                            // selected ATOMIC catalogue master onto the line.
                                            capabilities: Array.isArray(item.capabilities) ? [...item.capabilities] : [],
                                        } : { capabilities: [] }),
                                        // Picking an atomic item or clearing the picker must
                                        // also clear any stale package context — otherwise the
                                        // submit guard would 400 against the inflated state.
                                        package_source_id: null,
                                        package_source_code: null,
                                        package_presentation_mode: null,
                                        package_consolidated_label: null,
                                        package_components: [],
                                    };
                                })}
                                testidPrefix="prop-li"
                                required
                            />
                        </div>
                    </div>

                    {/* Iter C.1.1 — Phase C package context. Renders only when
                        the operator has picked a package catalogue item.
                        Atomic items continue with the original flow below. */}
                    {form.package_source_id && (
                        <ProposalPackageContext
                            sourceLabel={form.product_name}
                            sourceCode={form.package_source_code}
                            components={form.package_components || []}
                            presentationMode={form.package_presentation_mode || "itemized"}
                            consolidatedLabel={form.package_consolidated_label || ""}
                            onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                            atomicMasters={atomicMasters}
                        />
                    )}
                    <div>
                        <Label className="text-xs font-semibold">Option label <span className="font-normal text-slate-400">(optional · short grouping name)</span></Label>
                        <Input
                            value={form.option_label}
                            onChange={(e) => setForm({ ...form, option_label: e.target.value.slice(0, 80) })}
                            className="mt-1.5"
                            placeholder='e.g. "Option A", "MF Back Office" — keep it short'
                            maxLength={80}
                            data-testid="prop-li-option-label-input"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                            Used as the section header in the proposal items table.
                            Keep under 80 characters. For detailed commercial or legal text, use "Option description" below.
                        </p>
                        <p className="text-[10px] text-slate-500 mt-1" data-testid="prop-li-option-label-visibility-appears">
                            <span className="font-semibold">Appears on:</span> Proposal · Proposal PDF · Purchase Order · Purchase Order PDF
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5" data-testid="prop-li-option-label-visibility-hidden">
                            <span className="font-semibold">Does not appear on:</span> Invoice · Credit Note · AMC
                        </p>
                    </div>
                    {/* Iter 66.14 — Long commercial/legal context for an option group.
                        Rendered ONCE under the section header (italic, normal case)
                        and never duplicated into per-option breakdown rows. */}
                    <div>
                        <Label className="text-xs font-semibold">Option description <span className="font-normal text-slate-400">(optional · paragraph allowed)</span></Label>
                        <Textarea
                            value={form.option_description || ""}
                            onChange={(e) => setForm({ ...form, option_description: e.target.value })}
                            className="mt-1.5"
                            placeholder='Detailed scope, exclusivity terms, data-access clauses — anything that needs a paragraph. Shown once under the section header.'
                            rows={3}
                            data-testid="prop-li-option-description-input"
                        />
                        <p className="text-[10px] text-slate-500 mt-1" data-testid="prop-li-option-description-visibility-appears">
                            <span className="font-semibold">Appears on:</span> Proposal · Proposal PDF · Purchase Order · Purchase Order PDF
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5" data-testid="prop-li-option-description-visibility-hidden">
                            <span className="font-semibold">Does not appear on:</span> Invoice · Credit Note · AMC
                        </p>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">
                            HSN / SAC code
                            <span className="font-normal text-slate-400 ml-1">(auto-filled from catalogue · override if needed)</span>
                        </Label>
                        <Input
                            value={form.hsn_sac_code || ""}
                            onChange={(e) => setForm({ ...form, hsn_sac_code: e.target.value.replace(/\s+/g, "") })}
                            placeholder="997331"
                            maxLength={20}
                            className="mt-1.5 font-mono"
                            data-testid="prop-li-hsn-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Type</Label>
                            <select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="prop-li-type-select">
                                <option>One-time</option>
                                <option>AMC</option>
                            </select>
                        </div>
                        {/* Iter 51.8 — Frequency is hidden for One-time items
                            (cannot be billed on a recurring cadence by definition). */}
                        {form.product_type !== "One-time" ? (
                            <div>
                                <Label className="text-xs font-semibold">Frequency</Label>
                                <select value={form.billing_frequency} onChange={(e) => setForm({ ...form, billing_frequency: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="prop-li-freq-select">
                                    <option>Annual</option>
                                    <option>Half-Yearly</option>
                                    <option>Quarterly</option>
                                    <option>Monthly</option>
                                </select>
                            </div>
                        ) : (
                            <div className="text-[10px] text-slate-400 pt-7 italic" data-testid="prop-li-freq-na">
                                Frequency does not apply to One-time items.
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Software Fee (₹)</Label>
                            <Input
                                required type="number" min="0" step="0.01"
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                className="mt-1.5 font-mono"
                                disabled={(form.commitments || []).length > 0}
                                data-testid="prop-li-amount-input"
                            />
                            {(form.commitments || []).length > 0 && (
                                <p className="text-[10px] text-amber-700 mt-1 italic">
                                    Auto-synced from commitment sum (R-B*).
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">AMC Fee (₹)</Label>
                            <Input
                                type="number" min="0" step="0.01"
                                value={form.amc_amount}
                                onChange={(e) => setForm({ ...form, amc_amount: e.target.value })}
                                className="mt-1.5 font-mono"
                                disabled={(form.commitments || []).length > 0}
                                data-testid="prop-li-amc-input"
                            />
                            {(form.commitments || []).length > 0 && (
                                <p className="text-[10px] text-amber-700 mt-1 italic">
                                    AMC moves into a commitment row below.
                                </p>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">GST %</Label>
                            <Input type="number" min="0" max="28" step="1" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="mt-1.5 font-mono" data-testid="prop-li-gst-input" />
                        </div>
                    </div>

                    {/* Iter C.2.1.d — Commercial Commitments (R-B*) authoring.
                        A Package may own 1..N typed commitments. Each carries
                        its own label, amount, cadence, GST/HSN, and activation
                        rule. When at least one commitment is present, the
                        line-level Software Fee + AMC Fee become read-only and
                        the server auto-syncs ``amount = sum(commitments)``.
                        No taxonomy enforcement (Layer 3 deferred). */}
                    <CommitmentsEditor
                        commitments={form.commitments || []}
                        onChange={(commitments) => setForm((p) => ({ ...p, commitments }))}
                        defaultGstPercent={Number(form.gst_percent) || 18}
                        defaultHsn={form.hsn_sac_code || "997331"}
                    />
                    {form.product_type === "AMC" && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Start date</Label>
                                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">End date</Label>
                                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <FormDialogCancel testId="prop-li-cancel-btn">Cancel</FormDialogCancel>
                        <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="prop-li-submit-btn">
                            {submitting ? "Saving..." : isEdit ? "Save changes" : "Add line"}
                        </Button>
                    </DialogFooter>
                </form>
            </FormDialog>
        </>
    );
}

function ApproveDialog({ proposalId, version, onDone }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/billing/proposal/${proposalId}/approve`, { version_id: version.id });
            toast.success(`V${version.version_number} approved · proposal locked`);
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to approve"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" disabled={!version?.line_items?.length} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="approve-btn">
                    <CheckCircle size={14} weight="fill" /> Approve V{version?.version_number}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-display">Approve V{version?.version_number}?</DialogTitle>
                    <DialogDescription>This locks the proposal. After approval, only conversion to PO is allowed. To make changes, you'd need to create a new proposal.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-confirm-btn">
                        {submitting ? "Approving..." : "Yes, approve & lock"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * CancelDialog — 2026-07-22 · Proposal Versioning v2.
 *
 * Presents the operator with an unambiguous choice at click time:
 *   ◉ Cancel selected version V{n} only  (SAFE, default)
 *   ○ Cancel the entire proposal          (destructive; guarded)
 *
 * Version-only cancel is disabled when:
 *   - the selected version is the approved version (would break the
 *     approval commit point — backend also refuses),
 *   - the selected version is already cancelled,
 *   - the selected version is the only remaining active version
 *     (would leave zero active versions; backend refuses).
 * In those cases the radio flips to the whole-proposal option and the
 * text explains why.
 *
 * Whole-proposal cancel additionally requires the operator to type the
 * proposal number verbatim as a "type-to-confirm" gate — this catches
 * fat-fingered clicks that meant version-only cancel.
 */
function CancelDialog({ proposalId, proposalNumber, version, versions, approvedVersionId, onDone }) {
    const [open, setOpen] = useState(false);
    const [scope, setScope] = useState("version"); // "version" | "proposal"
    const [reason, setReason] = useState("");
    const [typedNumber, setTypedNumber] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef("");

    const isCurrentApproved = version.id === approvedVersionId;
    const isCurrentCancelled = (version.status || "active") === "cancelled";
    const activeCount = versions.filter((v) => (v.status || "active") !== "cancelled").length;
    const wouldLeaveZeroActive = activeCount <= 1 && !isCurrentCancelled;

    // If version-only cancel is not permitted, force the radio to "proposal"
    // when the dialog opens so the operator can't submit something the
    // backend will refuse.
    const versionScopeDisabled = isCurrentApproved || isCurrentCancelled || wouldLeaveZeroActive;

    useEffect(() => {
        if (open) {
            setScope(versionScopeDisabled ? "proposal" : "version");
            setReason("");
            setTypedNumber("");
            baselineRef.current = "";
        }
    }, [open, versionScopeDisabled]);

    const isDirty = reason !== baselineRef.current || scope !== "version" || typedNumber !== "";

    const canSubmit = !submitting
        && (scope === "version"
            ? !versionScopeDisabled
            : typedNumber.trim() === (proposalNumber || "").trim());

    const submit = async () => {
        setSubmitting(true);
        try {
            if (scope === "version") {
                await api.post(
                    `/billing/proposal/${proposalId}/version/${version.id}/cancel`,
                    { reason },
                );
                toast.success(`V${version.version_number} cancelled — other versions remain active`);
            } else {
                await api.post(
                    `/billing/proposal/${proposalId}/cancel`,
                    { reason, confirm: true },
                );
                toast.success("Proposal cancelled");
            }
            baselineRef.current = reason; // bypass discard prompt
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Cancel failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                onClick={() => setOpen(true)}
                className="gap-1.5 text-slate-700"
                data-testid="cancel-btn"
            >
                <XCircle size={14} /> Cancel
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId="proposal-cancel-dialog"
                title={`Cancelling V${version.version_number}`}
                description="Choose what to cancel. Safe default is the selected version only."
            >
                <div className="space-y-3">
                    {/* Version-only option */}
                    <label
                        className={cn(
                            "flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors",
                            scope === "version"
                                ? "border-slate-900 bg-slate-50"
                                : "border-slate-200 hover:border-slate-400",
                            versionScopeDisabled ? "opacity-60 cursor-not-allowed" : "",
                        )}
                        data-testid="cancel-scope-version"
                    >
                        <input
                            type="radio"
                            name="cancel-scope"
                            value="version"
                            checked={scope === "version"}
                            disabled={versionScopeDisabled}
                            onChange={() => setScope("version")}
                            className="mt-1"
                        />
                        <div className="flex-1">
                            <div className="text-sm font-semibold text-slate-900">
                                Cancel Version V{version.version_number} only
                                <span className="ml-2 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-slate-900 text-white uppercase">
                                    Safe · default
                                </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                                Marks V{version.version_number} as <strong>cancelled</strong>. Other versions and the proposal
                                itself stay untouched. The proposal remains in status <em>{versions.length ? "Draft" : "—"}</em>.
                            </div>
                            {versionScopeDisabled && (
                                <div className="text-[11px] text-rose-700 mt-1.5" data-testid="cancel-scope-version-disabled">
                                    {isCurrentApproved
                                        ? "This is the approved version — cancel the whole proposal instead."
                                        : isCurrentCancelled
                                            ? "This version is already cancelled."
                                            : "This is the only remaining active version — cancel the whole proposal instead."}
                                </div>
                            )}
                        </div>
                    </label>

                    {/* Whole-proposal option */}
                    <label
                        className={cn(
                            "flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors",
                            scope === "proposal"
                                ? "border-rose-500 bg-rose-50/40"
                                : "border-slate-200 hover:border-slate-400",
                        )}
                        data-testid="cancel-scope-proposal"
                    >
                        <input
                            type="radio"
                            name="cancel-scope"
                            value="proposal"
                            checked={scope === "proposal"}
                            onChange={() => setScope("proposal")}
                            className="mt-1"
                        />
                        <div className="flex-1">
                            <div className="text-sm font-semibold text-slate-900">
                                Cancel the entire proposal
                                <span className="ml-2 text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-rose-600 text-white uppercase">
                                    Destructive
                                </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1 leading-relaxed">
                                Sets the proposal status to <strong>Cancelled</strong> and marks every active version as
                                cancelled. Distinct from "Reject" (which represents client-side rejection).
                            </div>
                            {scope === "proposal" && (
                                <div className="mt-2 space-y-1.5">
                                    <Label className="text-xs font-medium text-slate-700">
                                        Type the proposal number to confirm: <span className="font-mono text-slate-900">{proposalNumber}</span>
                                    </Label>
                                    <Input
                                        value={typedNumber}
                                        onChange={(e) => setTypedNumber(e.target.value)}
                                        placeholder={proposalNumber || ""}
                                        className="font-mono"
                                        data-testid="cancel-proposal-typed-number"
                                    />
                                </div>
                            )}
                        </div>
                    </label>

                    {/* Reason (shared) */}
                    <div>
                        <Label className="text-xs font-medium text-slate-700">Reason (logged in audit)</Label>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            placeholder="Optional — but strongly recommended for future reference"
                            data-testid="cancel-reason-input"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <FormDialogCancel testId="cancel-dialog-close">Keep it</FormDialogCancel>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit}
                        className={cn(
                            "text-white",
                            scope === "proposal"
                                ? "bg-rose-600 hover:bg-rose-700"
                                : "bg-slate-900 hover:bg-slate-800",
                        )}
                        data-testid="cancel-confirm-btn"
                    >
                        {submitting
                            ? "Cancelling…"
                            : scope === "version"
                                ? `Cancel V${version.version_number} only`
                                : "Cancel entire proposal"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}


function RejectDialog({ proposalId, onDone }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const baselineRef = useRef("");

    useEffect(() => {
        if (open) {
            setReason("");
            baselineRef.current = "";
        }
    }, [open]);

    const isDirty = reason !== baselineRef.current;

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/billing/proposal/${proposalId}/reject`, { reason });
            toast.success("Proposal rejected");
            baselineRef.current = reason;  // bypass discard prompt
            setOpen(false);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to reject"));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <>
            <Button
                size="sm"
                variant="outline"
                onClick={() => setOpen(true)}
                className="gap-1.5 text-rose-700"
                data-testid="reject-btn"
            >
                <XCircle size={14} /> Reject
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId="proposal-reject-dialog"
                title="Reject proposal"
                description="Logged in audit. Cannot be undone."
            >
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason for rejection (logged)" data-testid="reject-reason-input" />
                <DialogFooter>
                    <FormDialogCancel testId="reject-cancel-btn">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={submitting} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="reject-confirm-btn">
                        {submitting ? "Rejecting..." : "Reject proposal"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

export default function ProposalDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [proposal, setProposal] = useState(null);
    const [poCoverage, setPoCoverage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [editingLineItem, setEditingLineItem] = useState(null);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [pdfDownloading, setPdfDownloading] = useState(false);
    // Iter 66.15.1 — feature-detect server-side PDF. When the backend returns
    // 503 (`PdfRenderUnavailable`, e.g. production image without Playwright),
    // we hide the "Download PDF" button for the rest of the session so the
    // operator silently falls back to the existing "Print / PDF" button.
    const [pdfFeatureAvailable, setPdfFeatureAvailable] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get(`/billing/proposal/${id}`);
            setProposal(data);
            // 2026-07-22 · Proposal Versioning v2 — default selection prefers:
            //   1. approved version (if any),
            //   2. else the max version_number among ACTIVE (non-cancelled) versions,
            //   3. else the max version_number outright (last-resort so a fully
            //      cancelled proposal still opens without a blank state).
            const versions = data.versions || [];
            const approved = data.approved_version_id
                ? versions.find((v) => v.id === data.approved_version_id)
                : null;
            const active = versions.filter((v) => (v.status || "active") !== "cancelled");
            const pool = active.length ? active : versions;
            const latest = pool.length
                ? pool.reduce((a, b) => (a.version_number > b.version_number ? a : b))
                : null;
            const def = approved || latest;
            setSelectedVersionId(def?.id || null);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load proposal"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const selectedVersion = useMemo(() =>
        proposal?.versions.find((v) => v.id === selectedVersionId) || null,
        [proposal, selectedVersionId]
    );

    const totals = useMemo(() => {
        if (!selectedVersion) return { sub: 0, gst: 0, total: 0 };
        // Iter C.1.2a — totals derived from EXPANDED display rows so the
        // footer matches the table. For atomic line items the expansion is
        // 1:1 with prior behaviour; for packages the rollup is across
        // package_components (mode-invariant: itemized/consolidated/hybrid
        // all sum to the same commercial value).
        let sub = 0, gst = 0;
        for (const row of expandLineItems(selectedVersion.line_items)) {
            const lineSub = row.software_fee + row.amc_fee + (Number(row.annual_cost_fee) || 0);
            sub += lineSub;
            if (row.gst_applicable) {
                gst += lineSub * (Number(row.gst_percent) || 0) / 100;
            }
        }
        return { sub, gst, total: sub + gst };
    }, [selectedVersion]);

    // Combined commercial value as shown in the footer (Special Offer applied).
    const payable = useMemo(() => {
        const so = proposal?.special_offer;
        if (!(so && so.enabled) || totals.sub <= 0) return { ...totals, taxable: totals.sub, offerActive: false };
        const taxable = Math.max(0, totals.sub - Number(so.discount_amount || 0));
        const gst = totals.gst * (taxable / totals.sub);
        return { sub: totals.sub, taxable, gst, total: taxable + gst, offerActive: true };
    }, [proposal?.special_offer, totals]);

    // Internal Year-1 Outflow — never leaves the UI (excluded from ProposalPrint).
    // Commencement per recurring charge = the line's start date, else the
    // confirmed coverage start on a linked PO. AMC with neither is "to be
    // confirmed" and makes the estimate provisional (never assumed year two).
    // Year 1 = 12 months from the earliest commencement (Setup and undated
    // Annual Cost start with the proposal). Package bundles keep their terms.
    const yearOne = useMemo(() => {
        const empty = { software: 0, amc: 0, amcLater: 0, amcLaterFrom: "", amcTbc: 0, annual: 0, annualLater: 0, anyAnnual: false, combined: 0, hasOptions: false, groups: new Map() };
        if (!selectedVersion) return empty;
        const lines = selectedVersion.line_items || [];
        const covered = {};
        if (poCoverage?.version_id === selectedVersion.id) {
            for (const c of poCoverage.charges || []) {
                const s = (c.allocations || []).map((a) => a.coverage_start).filter(Boolean).sort()[0];
                if (s) covered[c.key] = s.slice(0, 10);
            }
        }
        const startOf = (li, bucket) => String(li.start_date || "").slice(0, 10) || covered[`${li.id}:${bucket}`] || "";
        const versionDate = String(selectedVersion.created_date || proposal?.created_date || "").slice(0, 10);
        const anchors = [];
        for (const li of lines) {
            const rows = expandLineItem(li);
            if (rows.some((r) => r.software_fee)) anchors.push(versionDate);
            if (rows.some((r) => r.annual_cost_fee)) anchors.push(startOf(li, "annual_cost") || versionDate);
            if (rows.some((r) => r.amc_fee) && startOf(li, "amc")) anchors.push(startOf(li, "amc"));
        }
        const yearEnd = annualPeriodEndIso(anchors.filter(Boolean).sort()[0] || versionDate);
        const inYearOne = (s) => !yearEnd || s <= yearEnd;
        const groups = new Map();
        const t = { software: 0, amc: 0, amcLater: 0, amcLaterFrom: "", amcTbc: 0, annual: 0, annualLater: 0 };
        for (const li of lines) {
            const amcStart = startOf(li, "amc");
            const annualStart = startOf(li, "annual_cost");
            const pkg = isPackageLine(li);
            for (const row of expandLineItem(li)) {
                const key = (row.option_label && row.option_label.trim()) || "__default__";
                if (!groups.has(key)) groups.set(key, { software: 0, amc: 0, annual: 0 });
                const g = groups.get(key);
                const ac = Number(row.annual_cost_fee) || 0;
                g.software += row.software_fee;
                t.software += row.software_fee;
                if (row.amc_fee) {
                    if (pkg || (amcStart && inYearOne(amcStart))) { g.amc += row.amc_fee; t.amc += row.amc_fee; }
                    else if (amcStart) { t.amcLater += row.amc_fee; if (!t.amcLaterFrom || amcStart < t.amcLaterFrom) t.amcLaterFrom = amcStart; }
                    else t.amcTbc += row.amc_fee;
                }
                if (!annualStart || inYearOne(annualStart)) { g.annual += ac; t.annual += ac; } else t.annualLater += ac;
            }
        }
        const hasOptions = [...groups.keys()].some((k) => k !== "__default__");
        const anyAnnual = lines.some(usesAnnualCost);
        return { ...t, anyAnnual, combined: t.software + t.amc + t.annual, hasOptions, groups };
    }, [selectedVersion, proposal?.created_date, poCoverage]);

    // 2026-07-22 · Proposal Versioning v2 — legacy `cloneVersion` handler
    // was removed alongside the "Clone version" button. New revisions are
    // raised implicitly by saving on any historical version through the
    // Commercial Builder (see ProposalBuilderPrototype → save-as-new).

    // Iter 66.15 — Server-side Playwright PDF download.
    // Bypasses the browser print dialog: the backend renders /print in
    // headless Chromium, returns the PDF stream, and we save it via a
    // synthesized anchor click.
    const downloadPdf = async () => {
        if (pdfDownloading) return;
        setPdfDownloading(true);
        const toastId = toast.loading("Generating PDF…");
        try {
            const resp = await api.get(`/billing/proposal/${id}/pdf`, {
                responseType: "blob",
                // PDF render goes through a headless browser → allow extra time
                // beyond the global 30s axios default before we give up.
                timeout: 60000,
            });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const safeNo = (proposal?.proposal_number || id).replace(/[\\/]/g, "_");
            const a = document.createElement("a");
            a.href = url;
            a.download = `REEDOS_Proposal_${safeNo}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success("PDF downloaded", { id: toastId });
        } catch (err) {
            // Iter 66.15.1 — graceful degrade if PDF backend isn't provisioned
            // (production image without Playwright/Chromium → 503).
            if (err?.response?.status === 503) {
                setPdfFeatureAvailable(false);
                toast.error("Server-side PDF isn't available on this deployment yet. Use the Print / PDF button.", { id: toastId });
            } else {
                toast.error(formatApiError(err, "Failed to generate PDF"), { id: toastId });
            }
        } finally {
            setPdfDownloading(false);
        }
    };

    const markSent = async () => {
        try {
            await api.post(`/billing/proposal/${id}/mark-sent`);
            toast.success("Marked as Sent");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to mark sent"));
        }
    };

    // 2026-08-05 · If a user edits the client (address, GST, contact
    // person…) AFTER a Draft proposal has been created, the proposal
    // keeps the stale ``bill_to`` snapshot it captured at creation
    // time.  This handler exposes the existing backend
    // /refresh-bill-to endpoint (Draft-only, capability-gated on the
    // BE side) so an operator can pull the latest client details into
    // the current Draft without recreating the proposal.
    const [refreshingBillTo, setRefreshingBillTo] = useState(false);
    const refreshBillTo = async () => {
        if (!window.confirm(
            "Pull the latest client details (name, GST, address, billing contact, Kind Attention) into this Draft proposal? Any manual edits to the bill-to block or Kind Attention will be overwritten.",
        )) return;
        setRefreshingBillTo(true);
        try {
            const { data } = await api.post(`/billing/proposal/${id}/refresh-bill-to`);
            const changed = data?.diff && Object.keys(data.diff).length;
            toast.success(
                changed
                    ? `Refreshed · ${Object.keys(data.diff).length} field(s) updated`
                    : "Refreshed · nothing changed (already in sync)",
            );
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to refresh bill-to snapshot"));
        } finally {
            setRefreshingBillTo(false);
        }
    };

    const removeLineItem = async (li) => {
        if (!window.confirm(`Remove "${li.product_name}" from this version?`)) return;
        try {
            await api.delete(
                `/billing/proposal/${proposal.id}/lineitem/${li.id}?version_id=${selectedVersionId}`,
            );
            toast.success("Line item removed");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to remove line item"));
        }
    };

    if (loading) return <div data-testid="proposal-detail-page"><PageHeader eyebrow="Proposal" title="Loading..." /></div>;
    if (!proposal) return <div className="p-8 text-sm text-slate-500">Proposal not found.</div>;

    const editable = proposal.status === "Draft";
    const canSend = proposal.status === "Draft" && proposal.versions.some((v) => v.line_items.length > 0);
    const canApprove = ["Draft", "Sent"].includes(proposal.status);
    const canReject = !["Converted", "Rejected"].includes(proposal.status);
    const canConvert = proposal.status === "Approved" && !proposal.converted_to_po_id;
    const badge = STATUS_BADGE_MAP[proposal.status] || { status: proposal.status };

    return (
        <div data-testid="proposal-detail-page">
            <PageHeader
                eyebrow={
                    <Link to="/proposals" className="inline-flex items-center gap-1 hover:text-slate-900">
                        <ArrowLeft size={12} /> Proposals
                    </Link>
                }
                title={
                    <span className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-2xl sm:text-3xl">{proposal.proposal_number}</span>
                        <StatusBadge {...badge}>{proposal.status}</StatusBadge>
                        {proposal.converted_to_po_id && (
                            <Link to={`/purchase-orders/${proposal.converted_to_po_id}`} className="text-xs font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 underline">
                                → View PO
                            </Link>
                        )}
                    </span>
                }
                description={
                    <span className="inline-flex items-center gap-2 text-xs flex-wrap" data-testid="proposal-header-meta">
                        <Buildings size={14} /> {proposal.client?.name}
                        {proposal.kind_attention && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="reedos-eyebrow">Kind Attn</span>
                                <span className="text-slate-700" data-testid="proposal-kind-attention">{proposal.kind_attention}</span>
                            </>
                        )}
                        {proposal.module_code && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="reedos-eyebrow">Module</span>
                                <span className="text-slate-700 font-mono" data-testid="proposal-module-code">{proposal.module_code}</span>
                            </>
                        )}
                        {proposal.currency && proposal.currency !== "INR" && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="reedos-eyebrow">Ccy</span>
                                <span className="text-slate-700 font-mono" data-testid="proposal-currency">{proposal.currency}</span>
                            </>
                        )}
                        {proposal.owner && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="reedos-eyebrow">Owner</span>
                                <OwnerCell
                                    owner={proposal.owner}
                                    testId="proposal-detail-owner"
                                    onReassignClick={isSuperAdmin ? () => setReassignOpen(true) : undefined}
                                />
                            </>
                        )}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <RoleGuard permission="po:write">
                            <Link to={`/proposals/${proposal.id}/builder?versionId=${selectedVersionId || ""}`}>
                                <Button variant="outline" size="sm" className="gap-1.5" data-testid="open-commercials-builder-btn">
                                    <Sparkle size={14} weight="fill" /> Commercials Builder
                                </Button>
                            </Link>
                        </RoleGuard>
                        <Link to={`/proposals/${proposal.id}/print`}>
                            <Button variant="outline" size="sm" className="gap-1.5" data-testid="print-proposal-btn-detail">
                                <Printer size={14} weight="fill" /> Print / PDF
                            </Button>
                        </Link>
                        {pdfFeatureAvailable && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                onClick={downloadPdf}
                                disabled={pdfDownloading}
                                data-testid="download-pdf-btn"
                                title="Server-rendered PDF (Iter 66.15)"
                            >
                                <Printer size={14} weight="fill" />
                                {pdfDownloading ? "Generating…" : "Download PDF"}
                            </Button>
                        )}
                        <RoleGuard permission="po:write">
                            {editable && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={refreshBillTo}
                                    disabled={refreshingBillTo}
                                    className="gap-1.5"
                                    data-testid="refresh-bill-to-btn"
                                    title="Pull the latest client name / GST / address / billing contact / Kind Attention into this Draft.  Client edits made after the proposal was created are NOT auto-synced — use this to bring them in."
                                >
                                    <ArrowClockwise size={14} className={refreshingBillTo ? "animate-spin" : ""} />
                                    {refreshingBillTo ? "Refreshing…" : "Refresh Client"}
                                </Button>
                            )}
                            {canSend && (
                                <Button size="sm" variant="outline" onClick={markSent} className="gap-1.5" data-testid="mark-sent-btn">
                                    <PaperPlaneTilt size={14} /> Mark as Sent
                                </Button>
                            )}
                            {canApprove && selectedVersion && <ApproveDialog proposalId={proposal.id} version={selectedVersion} onDone={load} />}
                            {canReject && <RejectDialog proposalId={proposal.id} onDone={load} />}
                            {/* 2026-07-22 · Proposal Versioning v2 — Cancel dialog with
                                two clear radio options: version-only (safe default) or
                                whole-proposal (requires explicit acknowledgement). */}
                            {editable && selectedVersion && (
                                <CancelDialog
                                    proposalId={proposal.id}
                                    proposalNumber={proposal.proposal_number}
                                    version={selectedVersion}
                                    versions={proposal.versions || []}
                                    approvedVersionId={proposal.approved_version_id}
                                    onDone={load}
                                />
                            )}
                            {canConvert && <ConvertChargesDialog proposal={proposal} onDone={(po) => navigate(`/purchase-orders/${po.id}`)} />}
                            {/* 2026-07-22 · Proposal Versioning v2 — the standalone
                                "Clone version" button has been retired. New revisions
                                are now raised implicitly through the Commercial Builder
                                (Save on a historical version creates the next version). */}
                        </RoleGuard>
                    </div>
                }
            />

            {/* PB-002 (Option C · V1 UX affordance) — Post-Approval revision guidance.
                Approved proposals are intentionally locked (V1-C.1 immutability). This
                banner tells the operator how to raise the next revision without any
                schema / lifecycle change. Full revision workflow (Option A) is queued
                for V1.1. */}
            {proposal.status === "Approved" && (
                <div className="px-6 sm:px-8 pt-6">
                    <div
                        className="reedos-card border border-emerald-200 bg-emerald-50/50 p-4 flex flex-col sm:flex-row items-start gap-3"
                        data-testid="approved-revision-guidance"
                    >
                        <Info size={20} weight="duotone" className="text-emerald-700 shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-1">
                            <div className="text-sm font-semibold text-emerald-900">
                                This proposal is Approved and locked.
                            </div>
                            <div className="text-xs text-emerald-800 leading-relaxed">
                                To submit a revised commercial, create a new proposal for this client.
                                Existing defaults will auto-load from the latest approved proposal.
                            </div>
                        </div>
                        <RoleGuard permission="po:write">
                            <Link
                                to={`/proposals?new=1&client_id=${proposal.client_id}`}
                                data-testid="create-new-proposal-for-client-link"
                            >
                                <Button
                                    size="sm"
                                    className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5 shrink-0"
                                    data-testid="create-new-proposal-for-client-btn"
                                >
                                    <Plus size={14} weight="bold" /> Create New Proposal for this Client
                                </Button>
                            </Link>
                        </RoleGuard>
                    </div>
                </div>
            )}

            {/* 2026-07-22 · Proposal Versioning v2 — Version History is now
                a full-width row above the two-column body so the table
                (Version No. · Date & Time · Created By · Status · Revision
                Note) has proper horizontal breathing room and doesn't get
                clipped inside the old 280px sidebar column. */}
            <div className="px-6 sm:px-8 pt-8">
                <VersionTimeline proposal={proposal} selectedId={selectedVersionId} onSelect={setSelectedVersionId} />
            </div>

            <div className="px-6 sm:px-8 py-8">
                {selectedVersion && (
                    <div className="space-y-6 animate-slide-up">
                        <HeaderEditor proposalId={proposal.id} version={selectedVersion} editable={editable} onSaved={load} />

                        <SignedProposalCard proposal={proposal} onChanged={load} />

                        <TncSetCard proposal={proposal} version={selectedVersion} editable={editable} onChanged={load} />

                        <CustomizationsCard proposalId={proposal.id} proposalStatus={proposal.status} onChanged={load} />

                        <div className="reedos-card">
                            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div>
                                    <div className="reedos-eyebrow flex items-center gap-2">
                                        Version {selectedVersion.version_number}
                                        {selectedVersion.id === proposal.approved_version_id && (
                                            <StatusBadge status="Locked" icon={<LockKey size={10} weight="fill" />}>APPROVED</StatusBadge>
                                        )}
                                    </div>
                                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                                        {selectedVersion.line_items.length} line item{selectedVersion.line_items.length !== 1 ? "s" : ""}
                                    </h3>
                                </div>
                                {editable && (
                                    <RoleGuard permission="po:write">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {/* Iter 97.10 — Old commercial-entry surfaces (Apply Template,
                                                Bulk Add, Add Line Item) retired. The Commercials Builder is
                                                now the single commercial-entry mechanism — launched via
                                                the "Commercials Builder" button on the page header. */}
                                            <Link to={`/proposals/${proposal.id}/builder?versionId=${selectedVersionId || ""}`}>
                                                <Button variant="outline" size="sm" className="gap-1.5" data-testid="open-commercials-builder-from-table-btn">
                                                    <Sparkle size={13} weight="fill" /> Edit Commercials in Builder
                                                </Button>
                                            </Link>
                                        </div>
                                    </RoleGuard>
                                )}
                            </div>
                            {selectedVersion.line_items.length === 0 ? (
                                <div className="px-5 py-12 text-center text-sm text-slate-500">No line items yet.</div>
                            ) : (
                                <>
                                    {/* ── Internal Year-1 Outflow strip — NEVER shown on client print ── */}
                                    <div
                                        className="border-t border-amber-300 bg-amber-50/40 px-5 py-4"
                                        data-testid="year-one-outflow-strip"
                                    >
                                        <div className="flex items-center justify-between gap-4 mb-3">
                                            <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-amber-700">
                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                Internal Estimate · Not shown on client PDF
                                            </div>
                                            {yearOne.hasOptions && (
                                                <span className="text-[10px] font-medium text-amber-700/80">{yearOne.groups.size} options</span>
                                            )}
                                        </div>
                                        <div className={`grid grid-cols-1 gap-3 ${yearOne.anyAnnual ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
                                            <div className="bg-white rounded-md border border-slate-200 px-3.5 py-3" data-testid="year-one-software">
                                                <div className="reedos-eyebrow text-slate-500">Software & Set-up · Year-1</div>
                                                <div className="reedos-data font-display font-bold text-slate-900 text-base mt-1">{formatINR(yearOne.software, proposal?.currency)}</div>
                                                <div className="text-[10px] text-slate-500 mt-0.5">One-time + first-year licenses (excl. GST)</div>
                                            </div>
                                            <div className="bg-white rounded-md border border-slate-200 px-3.5 py-3" data-testid="year-one-amc">
                                                <div className="reedos-eyebrow text-slate-500">AMC · Year-1</div>
                                                <div className="reedos-data font-display font-bold text-slate-900 text-base mt-1">{formatINR(yearOne.amc, proposal?.currency)}</div>
                                                <div className="text-[10px] text-slate-500 mt-0.5" data-testid="year-one-amc-later">
                                                    {[
                                                        yearOne.amcTbc > 0 && `${formatINR(yearOne.amcTbc, proposal?.currency)} — AMC commencement to be confirmed`,
                                                        yearOne.amcLater > 0 && `${formatINR(yearOne.amcLater, proposal?.currency)} starts ${formatDate(yearOne.amcLaterFrom)} — after Year 1`,
                                                    ].filter(Boolean).join(" · ") || "AMC starting in Year 1 (excl. GST)"}
                                                </div>
                                            </div>
                                            {yearOne.anyAnnual && (
                                                <div className="bg-white rounded-md border border-sky-200 px-3.5 py-3" data-testid="year-one-annual-cost">
                                                    <div className="reedos-eyebrow text-slate-500">Annual Cost · Year-1</div>
                                                    <div className="reedos-data font-display font-bold text-slate-900 text-base mt-1">{formatINR(yearOne.annual, proposal?.currency)}</div>
                                                    <div className="text-[10px] text-slate-500 mt-0.5">
                                                        {yearOne.annualLater > 0 ? `${formatINR(yearOne.annualLater, proposal?.currency)} starts after Year 1` : "Data / hosting / server subscriptions (excl. GST)"}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="bg-slate-900 text-white rounded-md px-3.5 py-3" data-testid="year-one-combined">
                                                <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-amber-300">
                                                    Year-1 Outflow · excl. GST
                                                    {yearOne.amcTbc > 0 && <span className="ml-2 rounded bg-amber-300 px-1 py-0.5 text-[9px] tracking-wider text-slate-900" data-testid="year-one-provisional">Provisional</span>}
                                                </div>
                                                <div className="reedos-data font-display font-bold text-lg mt-1">{formatINR(yearOne.combined, proposal?.currency)}</div>
                                                <div className="text-[10px] text-slate-400 mt-0.5">
                                                    {yearOne.amcTbc > 0
                                                        ? `Incomplete — excludes ${formatINR(yearOne.amcTbc, proposal?.currency)} AMC until its commencement is confirmed`
                                                        : yearOne.anyAnnual ? "Software + Annual Cost + AMC starting in Year 1" : "Software + AMC starting in Year 1"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-700" data-testid="year-one-commercial-total">
                                            <span className="reedos-eyebrow text-slate-500">Combined commercial total{payable.offerActive ? " · after special offer" : ""}</span>
                                            <span>excl. GST <span className="reedos-data font-semibold text-slate-900" data-testid="year-one-commercial-excl">{formatINR(payable.taxable, proposal?.currency)}</span></span>
                                            <span>incl. GST <span className="reedos-data font-semibold text-slate-900" data-testid="year-one-commercial-incl">{formatINR(payable.total, proposal?.currency)}</span></span>
                                        </div>
                                        {yearOne.hasOptions && (
                                            <div className="mt-3 rounded-md border border-amber-200 bg-white overflow-hidden" data-testid="year-one-by-option">
                                                <table className="w-full text-xs">
                                                    <thead className="bg-amber-50 border-b border-amber-200">
                                                        <tr className="text-left">
                                                            <th className="px-3 py-2 reedos-eyebrow text-slate-600">Per-option breakdown</th>
                                                            <th className="px-3 py-2 reedos-eyebrow text-slate-600 text-right">Software</th>
                                                            <th className="px-3 py-2 reedos-eyebrow text-slate-600 text-right">AMC · Year-1</th>
                                                            {yearOne.anyAnnual && <th className="px-3 py-2 reedos-eyebrow text-slate-600 text-right">Annual Cost</th>}
                                                            <th className="px-3 py-2 reedos-eyebrow text-slate-600 text-right">Year-1 Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {[...yearOne.groups.entries()].map(([label, g]) => (
                                                            <tr key={label} className="border-t border-amber-100" data-testid={`year-one-option-row-${label}`}>
                                                                <td className="px-3 py-2 font-medium text-slate-900 max-w-[280px]" title={label === "__default__" ? "" : label}>
                                                                    {label === "__default__" ? <span className="text-slate-500">Unassigned</span> : truncateLabel(label, 60)}
                                                                </td>
                                                                <td className="px-3 py-2 text-right reedos-data">{formatINR(g.software, proposal?.currency)}</td>
                                                                <td className="px-3 py-2 text-right reedos-data">{formatINR(g.amc, proposal?.currency)}</td>
                                                                {yearOne.anyAnnual && <td className="px-3 py-2 text-right reedos-data">{formatINR(g.annual, proposal?.currency)}</td>}
                                                                <td className="px-3 py-2 text-right reedos-data font-semibold">{formatINR(g.software + g.amc + g.annual, proposal?.currency)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>

                                    <table className="w-full text-sm" data-testid="proposal-lines-table">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr className="text-left">
                                            <th className="px-4 py-3 reedos-eyebrow">Item</th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right">Amount</th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right">AMC</th>
                                            {yearOne.anyAnnual && <th className="px-4 py-3 reedos-eyebrow text-right" data-testid="prop-annual-cost-heading">Annual Cost</th>}
                                            <th className="px-4 py-3 reedos-eyebrow text-right">GST</th>
                                            <th className="px-4 py-3 reedos-eyebrow text-right">Line total</th>
                                            {/* Iter 97.10 — Actions column retired. Inline per-line
                                                edit/delete is replaced by the Commercials Builder
                                                round-trip; the column is no longer rendered. */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            // Iter C.1.2a — Expand each line item into 1..N display rows
                                            // based on its package_presentation_mode. Atomic lines remain
                                            // 1:1. Package lines fan out into itemized component rows /
                                            // a single consolidated row / hybrid visible+rollup pattern.
                                            // Edit & Remove still target the PARENT line item — the
                                            // package is edited as a unit.
                                            // Iter 97.10 — Actions column retired (Builder is the only
                                            // commercial-entry surface). Column count is now always 5.
                                            const colSpan = yearOne.anyAnnual ? 6 : 5;
                                            // Iter 97.15 — Unified 3-mode rendering for Builder-materialised
                                            // proposals (operator directive 2026-02-27). When ANY line
                                            // carries `presentation_mode` (set by the Builder), we apply
                                            // identical price-visibility rules to the Builder Preview:
                                            //   - Itemized     → all prices visible, subtotals visible
                                            //   - Grouped      → module-row prices BLANK, subtotals visible
                                            //   - Consolidated → all row prices BLANK, subtotals hidden
                                            // Blank cells are empty strings — no dash, no zero.
                                            const builderMode = (selectedVersion.line_items.find((li) => li.presentation_mode) || {}).presentation_mode;
                                            const builderShowModulePrices = builderMode === "itemized";
                                            const builderShowAtomicPrices = builderMode !== "consolidated";
                                            const builderShowSubtotals = builderMode && builderMode !== "consolidated";
                                            const formatBuilderCell = (val, visible) => {
                                                if (!visible) return "";
                                                return val > 0 ? formatINR(val, proposal?.currency) : "";
                                            };
                                            // Complimentary marker (2026-07-23) — Build a per-line
                                            // lookup for Setup/AMC "complimentary" flags from
                                            // `selectedVersion.builder_state.scope[*]`. Matches by
                                            // product_name against `moduleLabel || productName`.
                                            // Absent builder_state or absent scope → empty map
                                            // → legacy render path preserved exactly.
                                            const scopeMarkersByLabel = new Map();
                                            for (const sr of (selectedVersion.builder_state?.scope || [])) {
                                                const label = sr.moduleLabel || sr.productName;
                                                if (!label) continue;
                                                scopeMarkersByLabel.set(label, {
                                                    setup: sr.setup_fee_pricing_mode === "complimentary",
                                                    amc: sr.amc_pricing_mode === "complimentary",
                                                });
                                            }
                                            // Map parent_id -> original li (for edit/delete dispatch).
                                            const parentById = new Map();
                                            for (const li of selectedVersion.line_items) parentById.set(li.id, li);
                                            // Group expanded rows by option_label, preserving the
                                            // original parent line-item order within each group.
                                            const groups = new Map();
                                            for (const li of selectedVersion.line_items) {
                                                const key = (li.option_label && li.option_label.trim()) || "__default__";
                                                if (!groups.has(key)) groups.set(key, []);
                                                for (const dr of expandLineItem(li)) groups.get(key).push(dr);
                                            }
                                            const hasOptions = [...groups.keys()].some((k) => k !== "__default__");
                                            const rows = [];
                                            for (const [label, displayRows] of groups.entries()) {
                                                if (hasOptions) {
                                                    // Shared option_description: any row in the group
                                                    // may carry it; first non-empty wins. Long commercial
                                                    // context renders ONCE under the header instead of
                                                    // every row's subtitle.
                                                    const optionDescription = (displayRows.find((x) => (x.option_description || "").trim()) || {}).option_description;
                                                    rows.push(
                                                        <tr key={`hdr-${label}`} className="bg-slate-900 text-white">
                                                            <td colSpan={colSpan} className="px-4 py-2 text-[11px] font-bold tracking-wide uppercase" data-testid={`option-group-${label}`} title={label === "__default__" ? "" : label}>
                                                                {label === "__default__" ? "Unassigned" : truncateLabel(label)}
                                                            </td>
                                                        </tr>
                                                    );
                                                    if (optionDescription) {
                                                        rows.push(
                                                            <tr key={`desc-${label}`} className="bg-slate-50">
                                                                <td colSpan={colSpan} className="px-4 py-2 text-[11px] text-slate-600 italic leading-relaxed normal-case whitespace-pre-wrap" data-testid={`option-group-desc-${label}`}>
                                                                    {optionDescription}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                }
                                                // Track which parent has already had a "Package head"
                                                // banner row emitted within this option group so we
                                                // can show it ONCE per package, not once per component.
                                                const emittedPkgHead = new Set();
                                                for (const dr of displayRows) {
                                                    const parentLi = parentById.get(dr.parent_id);
                                                    const isPkgRow = dr.is_derived && parentLi;
                                                    if (isPkgRow && !emittedPkgHead.has(dr.parent_id)) {
                                                        emittedPkgHead.add(dr.parent_id);
                                                        const modeLabel = (parentLi.package_presentation_mode || "itemized")
                                                            .replace(/^[a-z]/, (m) => m.toUpperCase());
                                                        rows.push(
                                                            <tr key={`pkghdr-${dr.parent_id}`} className="bg-violet-50/70 border-t border-violet-200" data-testid={`pkg-head-${dr.parent_id}`}>
                                                                <td colSpan={colSpan} className="px-4 py-2">
                                                                    <div className="flex items-center justify-between gap-3">
                                                                        <div className="flex flex-col min-w-0 gap-1">
                                                                            <div className="flex items-center gap-2 min-w-0">
                                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-[0.16em] uppercase bg-violet-200 text-violet-900">Package</span>
                                                                                <span className="font-semibold text-slate-900 truncate">{parentLi.product_name}</span>
                                                                                <span className="text-[10px] text-slate-500">· {modeLabel}{parentLi.package_presentation_mode === "consolidated" && parentLi.package_consolidated_label ? ` · "${parentLi.package_consolidated_label}"` : ""}</span>
                                                                            </div>
                                                                            {/* Iter C.2.1.CAP — Capability badges on package head. */}
                                                                            <CapabilityBadges capabilities={parentLi.capabilities} size="sm" />
                                                                            {/* Iter C.2.1.d — Commitments breakdown chip strip */}
                                                                            <CommitmentsBadgeStrip commitments={parentLi.commitments} />
                                                                        </div>
                                                                        {/* Iter 97.10 — Inline pkg edit/delete retired. Use the Commercials Builder. */}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }

                                                    const lineSub = dr.software_fee + dr.amc_fee + (Number(dr.annual_cost_fee) || 0);
                                                    const lineTotal = lineSub * (1 + (dr.gst_applicable ? (Number(dr.gst_percent) || 0) / 100 : 0));
                                                    // Iter 97.15 — Per-mode price visibility for Builder rows.
                                                    // For Builder-materialised lines, derive cell visibility
                                                    // from row_kind × presentation_mode. Non-Builder (legacy)
                                                    // rows fall through to the original "—" placeholder.
                                                    const isBuilderRow = !!dr.presentation_mode;
                                                    const builderRowKind = dr.row_kind;
                                                    const isProductHeader = isBuilderRow && builderRowKind === "product_header";
                                                    // Iter 107.1 — Product-header rows are non-commercial
                                                    // grouping rows: hide GST% and Line Total so headers
                                                    // don't inherit numeric commercials from their children.
                                                    // Parity fix with the PO/Invoice regression request —
                                                    // headers render only the item name/description column.
                                                    const showSetupCell = !isBuilderRow
                                                        || (builderRowKind === "module" ? builderShowModulePrices
                                                            : builderRowKind === "atomic" ? builderShowAtomicPrices
                                                                : false);  // product_header
                                                    const showAmcCell = showSetupCell;
                                                    const showLineTotalCell = showSetupCell;
                                                    const setupCellContent = isBuilderRow
                                                        ? formatBuilderCell(dr.software_fee, showSetupCell)
                                                        : (dr.software_fee > 0 ? formatINR(dr.software_fee, proposal?.currency) : <span className="text-slate-300">—</span>);
                                                    const amcCellContent = isBuilderRow
                                                        ? formatBuilderCell(dr.amc_fee, showAmcCell)
                                                        : (dr.amc_fee > 0 ? formatINR(dr.amc_fee, proposal?.currency) : <span className="text-slate-300">—</span>);
                                                    // Complimentary marker override (2026-07-23) —
                                                    // The marker's source-of-truth is the presence
                                                    // of the flag on builder_state.scope[*]. This is
                                                    // ALWAYS a Builder-authored artefact — legacy
                                                    // proposals have no scope entries so the map is
                                                    // empty and this branch is a no-op. Do NOT gate
                                                    // on `isBuilderRow` (which requires
                                                    // presentation_mode on each line_item — the
                                                    // Builder's own materialiser sets it, but data
                                                    // written by other flows may not).
                                                    const dispMarkers = !isProductHeader
                                                        ? scopeMarkersByLabel.get(dr.product_name)
                                                        : null;
                                                    const finalSetupCellContent = (dispMarkers?.setup)
                                                        ? <span className="text-indigo-700 font-medium italic" data-testid={`prop-line-setup-complimentary-${dr.id}`}>Complimentary</span>
                                                        : setupCellContent;
                                                    const finalAmcCellContent = (dispMarkers?.amc)
                                                        ? <span className="text-indigo-700 font-medium italic" data-testid={`prop-line-amc-complimentary-${dr.id}`}>Complimentary</span>
                                                        : amcCellContent;
                                                    const lineTotalCellContent = isBuilderRow
                                                        ? (showLineTotalCell && lineTotal > 0 ? formatINR(lineTotal, proposal?.currency) : "")
                                                        : formatINR(lineTotal, proposal?.currency);
                                                    const rowClass = isPkgRow
                                                        ? (dr.kind === "pkg-hybrid-rollup"
                                                            ? "border-t border-slate-100 bg-amber-50/30"
                                                            : "border-t border-slate-100 bg-violet-50/20")
                                                        : (builderRowKind === "module"
                                                            ? "border-t border-slate-100 bg-slate-50/40"
                                                            : builderRowKind === "product_header"
                                                                ? "border-t border-slate-200 bg-slate-50"
                                                                : "border-t border-slate-100");
                                                    rows.push(
                                                        <tr key={dr.id} className={rowClass} data-testid={`prop-line-row-${dr.id}`}>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-start gap-2">
                                                                    {(isPkgRow || builderRowKind === "module") && (
                                                                        <span aria-hidden className="text-slate-400 text-xs leading-5 select-none">↳</span>
                                                                    )}
                                                                    <div className="min-w-0">
                                                                        <div className={`font-medium text-slate-900 truncate ${builderRowKind === "product_header" ? "font-semibold" : ""}`}>{dr.product_name}</div>
                                                                        {dr.product_subtitle && (
                                                                            <div className="text-[11px] text-slate-500 mt-0.5">{dr.product_subtitle}</div>
                                                                        )}
                                                                        {/* Iter 97.16 — Delivery-mode icons under Builder
                                                                            product_header + atomic rows (operator parity
                                                                            with Builder Preview). Module rows never carry
                                                                            their own modes — modes belong to the parent
                                                                            product. */}
                                                                        {isBuilderRow && (builderRowKind === "product_header" || builderRowKind === "atomic") && Array.isArray(dr.delivery_modes) && dr.delivery_modes.length > 0 && (
                                                                            <div className="flex items-center gap-1.5 mt-1 text-slate-600" data-testid={`prop-line-delivery-${dr.id}`}>
                                                                                {dr.delivery_modes.map((c) => {
                                                                                    const dm = DELIVERY_MODES.find((x) => x.code === c);
                                                                                    if (!dm) return null;
                                                                                    return <DeliveryIcon key={c} iconName={dm.iconName} label={dm.label} size={13} />;
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                        {/* Iter C.2.1.CAP — Capability badges on atomic lines. */}
                                                                        {!isPkgRow && !isBuilderRow && (
                                                                            <CapabilityBadges capabilities={parentLi?.capabilities} size="sm" className="mt-1" />
                                                                        )}
                                                                        {/* Iter C.2.1.d — Commitments chip strip on atomic lines. */}
                                                                        {!isPkgRow && !isBuilderRow && (
                                                                            <CommitmentsBadgeStrip commitments={parentLi?.commitments} />
                                                                        )}
                                                                        {Array.isArray(dr.scope_items) && dr.scope_items.length > 0 && (
                                                                            <div className="mt-1.5" data-testid={`prop-line-scope-${dr.id}`}>
                                                                                <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Includes</div>
                                                                                <ul className="mt-0.5 space-y-0.5 text-[12px] text-slate-700">
                                                                                    {dr.scope_items.map((s, i) => (
                                                                                        <li key={i} className="flex items-baseline gap-1.5">
                                                                                            <span className="text-slate-400">•</span>
                                                                                            <span className="font-medium">{s.module_label}</span>
                                                                                            {s.channels && (
                                                                                                <span className="text-slate-500 text-[11px]">({formatScopeChannels(s.channels)})</span>
                                                                                            )}
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right reedos-data text-sm font-semibold">{finalSetupCellContent}</td>
                                                            <td className="px-4 py-3 text-right reedos-data text-sm" data-testid={`prop-line-amc-${dr.id}`}>
                                                                {finalAmcCellContent}
                                                            </td>
                                                            {yearOne.anyAnnual && (
                                                                <td className="px-4 py-3 text-right reedos-data text-sm" data-testid={`prop-line-annual-cost-${dr.id}`}>
                                                                    {dr.annual_cost_complimentary
                                                                        ? <span className="text-indigo-700 font-medium italic">Complimentary</span>
                                                                        : (isBuilderRow ? formatBuilderCell(dr.annual_cost_fee, showSetupCell)
                                                                            : (dr.annual_cost_fee > 0 ? formatINR(dr.annual_cost_fee, proposal?.currency) : <span className="text-slate-300">—</span>))}
                                                                </td>
                                                            )}
                                                            <td className="px-4 py-3 text-right text-xs text-slate-600">{isProductHeader ? "" : (dr.gst_applicable ? `${dr.gst_percent}%` : "Excl.")}</td>
                                                            <td className="px-4 py-3 text-right reedos-data text-sm font-semibold">{lineTotalCellContent}</td>
                                                            {/* Iter 97.10 — Inline edit/delete actions retired (Builder is the only commercial-entry path). */}
                                                        </tr>
                                                    );
                                                }
                                                // Iter 97.15 — Per-band subtotal row for Builder rows
                                                // (Itemized + Grouped only). Hidden in Consolidated.
                                                if (builderMode && builderShowSubtotals && label !== "__default__") {
                                                    let subSetup = 0;
                                                    let subAmc = 0;
                                                    let subAnnual = 0;
                                                    for (const dr of displayRows) {
                                                        subSetup += Number(dr.software_fee) || 0;
                                                        subAmc += Number(dr.amc_fee) || 0;
                                                        subAnnual += Number(dr.annual_cost_fee) || 0;
                                                    }
                                                    if (subSetup > 0 || subAmc > 0 || subAnnual > 0) {
                                                        rows.push(
                                                            <tr key={`sub-${label}`} className="border-t border-slate-200 bg-slate-50 font-semibold" data-testid={`prop-band-subtotal-${label}`}>
                                                                <td className="px-4 py-2 text-right text-[11px] uppercase tracking-wide text-slate-600">Subtotal — {truncateLabel(label)}</td>
                                                                <td className="px-4 py-2 text-right reedos-data text-sm" data-testid={`prop-band-setup-${label}`}>{subSetup > 0 ? formatINR(subSetup, proposal?.currency) : ""}</td>
                                                                <td className="px-4 py-2 text-right reedos-data text-sm" data-testid={`prop-band-amc-${label}`}>{subAmc > 0 ? formatINR(subAmc, proposal?.currency) : ""}</td>
                                                                {yearOne.anyAnnual && <td className="px-4 py-2 text-right reedos-data text-sm" data-testid={`prop-band-annual-cost-${label}`}>{subAnnual > 0 ? formatINR(subAnnual, proposal?.currency) : ""}</td>}
                                                                <td></td>
                                                                <td className="px-4 py-2 text-right reedos-data text-sm">{(subSetup + subAmc + subAnnual) > 0 ? formatINR(subSetup + subAmc + subAnnual, proposal?.currency) : ""}</td>
                                                            </tr>
                                                        );
                                                    }
                                                }
                                            }
                                            return rows;
                                        })()}
                                    </tbody>
                                    <tfoot className="bg-slate-50 border-t border-slate-200">
                                        {/* Iter 67 — Special-Offer-aware footer.
                                            When a frozen offer is applied on the
                                            proposal, render the five-row
                                            commercial summary so the
                                            Proposal → PO → Invoice chain shows
                                            identical figures end-to-end. GST is
                                            scaled proportionally to the net
                                            taxable base (same allocation the
                                            backend applies at convert-to-PO).

                                            PROPOSAL_BUILDER_SPEC_V1 § V1-5.4
                                            (2026-07-01, Rahul-approved drift fix):
                                            In Consolidated presentation mode the
                                            tfoot collapses to a SINGLE Grand
                                            Total row (matching Preview + Print).
                                            Discount breakdown remains visible via
                                            the SpecialOfferEditor card below the
                                            table, so no operator-visible info is
                                            lost. Contract test:
                                            test_proposal_builder_contract_v1.
                                            test_consolidated_mode_detail_footer_collapses. */}
                                        {(() => {
                                            const so = proposal?.special_offer;
                                            const { sub, gst, total, offerActive } = payable;
                                            const consolidatedBuilderMode =
                                                (selectedVersion.line_items || [])
                                                    .some((li) => li.presentation_mode === "consolidated");
                                            if (consolidatedBuilderMode) {
                                                return (
                                                    <tr
                                                        className="border-t border-slate-200 bg-slate-100 font-semibold"
                                                        data-testid="proposal-footer-consolidated-grand-total"
                                                    >
                                                        <td
                                                            colSpan={yearOne.anyAnnual ? 5 : 4}
                                                            className="px-4 py-3 text-right text-[11px] uppercase tracking-wide text-slate-700"
                                                        >
                                                            Grand Total
                                                        </td>
                                                        <td
                                                            className="px-4 py-3 text-right reedos-data text-sm font-semibold"
                                                            data-testid="proposal-footer-consolidated-grand-total-value"
                                                        >
                                                            {formatINR(total, proposal?.currency)}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            return (
                                                <CommercialSummaryRows
                                                    subtotal={sub}
                                                    gst={gst}
                                                    total={total}
                                                    specialOffer={offerActive ? so : null}
                                                    currency={proposal?.currency}
                                                    colSpan={yearOne.anyAnnual ? 4 : 3}
                                                    testIdPrefix="proposal-footer"
                                                />
                                            );
                                        })()}
                                    </tfoot>
                                </table>
                                {/* Iter 97.16 — Delivery-mode legend mirroring
                                    the Builder Preview. Enumerates only the
                                    modes actually used across the proposal's
                                    Builder-materialised lines (no dead
                                    vocabulary). Hidden when no Builder lines
                                    carry delivery_modes. */}
                                {(() => {
                                    const used = new Set();
                                    for (const li of selectedVersion.line_items || []) {
                                        if (!li.presentation_mode) continue;
                                        for (const c of (li.delivery_modes || [])) used.add(c);
                                    }
                                    if (used.size === 0) return null;
                                    const visible = DELIVERY_MODES.filter((d) => used.has(d.code));
                                    return (
                                        <div className="border-t border-slate-200 bg-slate-50/60 px-5 py-3" data-testid="prop-delivery-legend">
                                            <div className="reedos-eyebrow text-slate-500 mb-2">Delivery Modes</div>
                                            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-700">
                                                {visible.map((dm) => (
                                                    <span key={dm.code} className="inline-flex items-center gap-1.5" data-testid={`prop-delivery-legend-${dm.code}`}>
                                                        <DeliveryIcon iconName={dm.iconName} label={dm.label} size={14} />
                                                        <span>{dm.label}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                                </>
                            )}
                        </div>

                        {/* Iter C.2.1.e — Special Offer card. Lives below the
                            line-items totals so the operator naturally scrolls
                            Subtotal → GST → Total → Special Offer. Switches
                            between editor (Draft) and frozen-display
                            (Sent / Approved / Converted) automatically. */}
                        {["Approved", "Converted"].includes(proposal.status) && (
                            <ProposalPoCoverage proposal={proposal} onChanged={load} onCoverage={setPoCoverage} />
                        )}
                        <SpecialOfferEditor
                            proposal={proposal}
                            editable={editable}
                            onChanged={load}
                        />
                    </div>
                )}
            </div>
            {/* Iter 97.10 — Controlled inline-edit dialog retired. The Commercials Builder is the only commercial-entry path. */}
            <ReassignOwnerDialog
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                entityLabel="Proposal"
                entityName={proposal.proposal_number}
                currentOwner={proposal.owner}
                reassignUrl={`/billing/proposal/${id}/owner`}
                onSuccess={load}
                testIdPrefix="proposal-detail-reassign"
            />
        </div>
    );
}
